import React, {
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useModals, useModalStack } from '../../../contexts/ModalControllerContext';
import { useHistory } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import './NewLandingPage.scss';
import MapOnboardingHint from './MapOnboarding/MapOnboardingHint';
import { aggregateBuildingFeatures } from './aggregateBuildingFeatures';
import {
  cancelPendingMapListingsSearch,
  fetchMapListingsPage,
} from './mapListingsRequest';
import { setServerSeenBuildings } from '../../HouseMap/seenBuildings';
import {
  buildBuildingIndex,
  buildingFromIndexEntry,
  GeometryCache,
} from './buildingIndex';
import homeIcon from '../../common/icons/house.png';
import coinIcon from './assets/coin-angled.png';
import BuildingMap, { BuildingMapHandle } from '../../HouseMap/BuildingMap';
import {
  addDemandLayers,
  refreshDemandLayers,
  orderDemandLayers,
  setDemandData,
  setAreaHighlight,
  setDemandVisible,
  circlePolygon,
  mergePolygons,
  neighbourhoodGeometry,
  DemandData,
} from '../../HouseMap/demandLayers';
import { apiRequest } from '../../utils/api';
import { createDemandRequest, mergeAppliedFilters } from './filterRequests';
import {
  fetchConversationIdMap,
  mergeConversationIds,
  useConversationBadgeRefreshOnLogin,
} from '../../utils/conversationBadges';
import { openBuildingInNewTab } from '../../utils/buildingLink';
import { useOpenListing } from '../../utils/useOpenListing';
import { createProfileLocationState } from '../../utils/profileRouteState';
import mapboxgl from 'mapbox-gl';
import {
  BuildingWithConvertedAddresses,
  LatLng,
  OffMarketWillingnessEvent,
} from '../../utils/types';
import BuildingPreviewCard from '../../BuildingPreviewCard/BuildingPreviewCard';
import { filterPreviewListings } from '../../BuildingPreviewCard/filterPreviewListings';
import {
  useBuildingPreview,
  BuildingPreviewSeed,
  BuildingPreviewMetaPatch,
} from '../../BuildingPreviewCard/useBuildingPreview';
import { useTranslation } from 'react-i18next';
import SearchInput from '../../SearchInput/SearchInput';
import { useMapListingViewport } from './useMapListingViewport';
import { useDeferredMapListingsRefresh } from './useDeferredMapListingsRefresh';
import { PreferredLocation, preferredLocationInView } from '../../SearchInput/preferredLocation';
import FilterPanel from '../../ExploreListings/FilterPanel';
import SearchResults from '../../ExploreListings/SearchResults';
import { activeDetailFields } from '../../ExploreListings/matchDetails';
import { ListingCardSkeleton } from '../../ListingCard';
import {
  SearchFilters,
  MapSearchPagination as SearchPagination,
  SearchResult,
} from '../../ExploreListings/types';
import { getCookie } from '../../utils/utils';
import { useGoogleMapsApi } from '../../../GoogleMapsApiContext';
import AddMyPlaceFlow, {
  AddMyPlaceSummary,
  AddMyPlaceSeed,
} from './AddMyPlaceFlow/AddMyPlaceFlow';
import CouldBuyFlow from './CouldBuyFlow/CouldBuyFlow';
import { CouldBuySeed, CouldBuyFormState } from './CouldBuyFlow/types';
import { areaIdKey } from './CouldBuyFlow/neighbourhoodApi';
import {
  AreaDemandCard,
  AreaInterest,
  formFromAreaInterest,
} from './CouldBuyFlow/areaInterestApi';
import DemandBuyerCard from './DemandBuyerCard';
import OffMarketToast from '../../common/OffMarketToast/OffMarketToast';
import FeedbackModal from '../../FeedbackModal/FeedbackModal';
import type LandingMenuModal from './LandingMenuModal';
import type { OffMarketToastMessage } from '../../utils/types';
import { localStore } from '../../utils/safeStorage';

const AMP_RESTORE_STORAGE_KEY = 'add_my_place_flow_restore';
const AMP_PAYMENT_QUERY_PARAM = 'amp-payment';

interface NewLandingPageProps {
  selectedBuilding: BuildingWithConvertedAddresses | null;
  selectBuilding: (building: BuildingWithConvertedAddresses) => void;
  userLocation: LatLng;
  setLoginModalVisible: (show: boolean) => void;
  isLoggedIn: boolean;
  hasCheckedLogin: boolean;
  userEmail: string | null;
  showRemoveListingDialog: string | null;
  setShowRemoveListingDialog: (listingId: string | null) => void;
  handleSelectBuildingFromExplore: (buildingId: string) => void;
  isAdmin: boolean;
  countryOfFeedContent: string;
  setCountryOfFeedContent: (country: string | null) => void;
  navViewActive?: boolean;
  onNavigateExplore: () => void;
  // Reports whether a full-screen landing overlay is open, so the shell can
  // hide the bottom nav on mobile (JAA-266).
  onOverlayVisibilityChange?: (open: boolean) => void;
  onRegisterPreviewMetaUpdater?: (
    updater:
      | ((buildingId: string, patch: BuildingPreviewMetaPatch) => void)
      | null,
  ) => void;
  onRegisterBuildingLikeAnimationHandler?: (
    handler: ((building: BuildingWithConvertedAddresses) => void) | null,
  ) => void;
  // Lets sibling surfaces (e.g. the building view's "Sell here") open the AMP
  // flow seeded with a known building. NewLandingPage owns the flow + all its
  // map/feed context, so it registers the launcher rather than duplicating it.
  onRegisterAddMyPlaceLauncher?: (
    launcher: ((seed: AddMyPlaceSeed | null) => void) | null,
  ) => void;
  // Lets the building view's "View map" hand its building back to the map this
  // page owns: centre on it and re-open its on-map preview.
  onRegisterMapFocusHandler?: (
    handler: ((buildingId: string, coords?: LatLng) => void) | null,
  ) => void;
  // Lets an area-interest listing return to the expanded Explore map without
  // treating its stored anchor building as the interest's location. The
  // optional centre is the interest's own representative map point.
  onRegisterShowMapHandler?: (
    handler: ((center?: LatLng) => void) | null,
  ) => void;
  // The logo is a global-home action, so from an open building view it first has
  // to close that view — AppContent owns its state.
  onCloseBuildingView?: () => void;
  // Cold building deep links open the building page immediately; while that page
  // covers the landing shell, skip spinning up the heavy Mapbox explore map.
  suspendMapWhileBuildingDeepLinked?: boolean;
  // True while the shared single-listing view (/listing/:id) owns the screen.
  // AppContent owns that route, so it has to tell the shell to drop its chrome
  // the same way the building view does. (JAA-306)
  listingViewOpen?: boolean;
}

const DEFAULT_SEARCH_FILTERS: SearchFilters = {
  includeIncompleteDetails: true,
  listingType: 'both',
  location: null,
  priceRange: { min: null, max: null },
  size: { min: null, max: null },
  rooms: null,
  floor: { min: null, max: null },
  renovationStatus: null,
  hasElevator: null,
  hasBalcony: null,
};

type MapBounds = { sw: [number, number]; ne: [number, number] };
type SearchResultBounds = MapBounds & { maxZoom: number };
type OffMarketFeatureCollection = GeoJSON.FeatureCollection;

type AreaLabel = {
  area: string | null;
  postal: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
};

const EMPTY_AREA_LABEL: AreaLabel = {
  area: null,
  postal: null,
  city: null,
  region: null,
  country: null,
};

// Pulls a sensible "area" name out of Google address components. Priority:
// neighborhood > sublocality (any level) > locality > administrative_area_level_1
// > country. The same parser is reused for both search-derived results and
// reverse-geocoded map centers.
const extractAreaLabel = (
  components: Array<{ long_name: string; types: string[] }> | undefined,
): AreaLabel => {
  if (!components) return EMPTY_AREA_LABEL;

  let neighborhood: string | null = null;
  let sublocality: string | null = null;
  let locality: string | null = null;
  let adminArea: string | null = null;
  let country: string | null = null;
  let postal: string | null = null;

  for (const c of components) {
    if (c.types.includes('neighborhood') && !neighborhood) {
      neighborhood = c.long_name;
    } else if (
      c.types.some((t) => t.startsWith('sublocality')) &&
      !sublocality
    ) {
      sublocality = c.long_name;
    } else if (c.types.includes('locality') && !locality) {
      locality = c.long_name;
    } else if (c.types.includes('administrative_area_level_1') && !adminArea) {
      adminArea = c.long_name;
    } else if (c.types.includes('country') && !country) {
      country = c.long_name;
    } else if (c.types.includes('postal_code') && !postal) {
      postal = c.long_name;
    }
  }

  return {
    area: neighborhood || sublocality || locality || adminArea || country,
    postal,
    city: locality,
    region: adminArea,
    country,
  };
};

const MAP_LISTINGS_FETCH_DEBOUNCE_MS = 300;
const MAP_LISTINGS_PAGE_LIMIT = 20;
// Everything the server's visibleCounts depend on. It counts both sides
// regardless of the listing-type filter (so the inactive pill never reads 0),
// which makes a sellers/buyers toggle the one refetch that always paints the
// same two numbers back — blanking them for it would flash a skeleton over
// values that never change. listingType is neutralized rather than omitted so
// any filter added later counts as counts-affecting by default.
const countsSignature = (bounds: MapBounds, filters: SearchFilters) =>
  JSON.stringify([bounds.sw, bounds.ne, { ...filters, listingType: null }]);
// One sellers/buyers number, or its placeholder while the counts reload — never
// both. The old cross-fade left the number in the layout at opacity 0, so the chip
// stayed sized by text nobody could see and clipped the placeholder on a narrow
// iPhone (JAA-324). The six call sites differ only in their wrapper element.
const countBody = (loading: boolean, label: string) =>
  loading ? (
    <span className="new-landing-page__sellers-buyers-skeleton" aria-hidden="true" />
  ) : (
    <span className="new-landing-page__sellers-buyers-count-value">{label}</span>
  );

const REVERSE_GEOCODE_DEBOUNCE_MS = 400;
const GEOLOCATE_LOADING_FALLBACK_MS = 15000;
const EXPLORE_INTRO_ANIMATION_MS = 1400;
const AREA_INTEREST_MAP_ZOOM = 14;
const FINLAND_LANDING_MAP_ZOOM = 12.176007976467098;
// How far around a point the explore search looks for district labels.
const NEARBY_PLACES_RADIUS_M = 3000;
// Zoom ladder for the info-panel header. As the user zooms out the label
// climbs neighborhood -> city -> region -> country -> continent -> World.
const AREA_LABEL_TIER_ZOOM = {
  city: 13, // below this -> city
  region: 10, // below this -> region (e.g. Uusimaa)
  country: 6.5, // below this -> country (e.g. Finland)
  continent: 4, // below this -> continent (e.g. Europe)
  world: 2.5, // below this -> World
};

type AreaTier =
  | 'neighborhood'
  | 'city'
  | 'region'
  | 'country'
  | 'continent'
  | 'world';

const resolveAreaTier = (zoom: number | null): AreaTier => {
  if (zoom === null || zoom >= AREA_LABEL_TIER_ZOOM.city) return 'neighborhood';
  if (zoom >= AREA_LABEL_TIER_ZOOM.region) return 'city';
  if (zoom >= AREA_LABEL_TIER_ZOOM.country) return 'region';
  if (zoom >= AREA_LABEL_TIER_ZOOM.continent) return 'country';
  if (zoom >= AREA_LABEL_TIER_ZOOM.world) return 'continent';
  return 'world';
};

// Country (English name, as used across the app and in getUserCountry.ts) to
// continent translation sub-key. Drives the continent zoom tier. Unmapped
// countries simply skip the continent tier (fall through to country / World).
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  Finland: 'europe',
  Sweden: 'europe',
  Norway: 'europe',
  Denmark: 'europe',
  Germany: 'europe',
  France: 'europe',
  'United Kingdom': 'europe',
  Spain: 'europe',
  Italy: 'europe',
  Netherlands: 'europe',
  Belgium: 'europe',
  Austria: 'europe',
  Switzerland: 'europe',
  Poland: 'europe',
  'Czech Republic': 'europe',
  Hungary: 'europe',
  Estonia: 'europe',
  Latvia: 'europe',
  Lithuania: 'europe',
  Portugal: 'europe',
  Russia: 'europe',
  'United States': 'northAmerica',
  Canada: 'northAmerica',
  Mexico: 'northAmerica',
  Brazil: 'southAmerica',
  Japan: 'asia',
  'South Korea': 'asia',
  China: 'asia',
  India: 'asia',
  Australia: 'oceania',
  'New Zealand': 'oceania',
};

const DESKTOP_LANDING_MAP_QUERY = '(min-width: 1600px)';
// The left explore panel (vs the mobile bottom sheet) is shown by CSS at
// min-width: 1025px. The on-map preview routing must use the SAME breakpoint so
// desktop drills the panel while mobile keeps the floating docked card.
const DESKTOP_EXPLORE_PANEL_QUERY = '(min-width: 1025px)';

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);

    update();

    mediaQuery.addEventListener?.('change', update);
    return () => {
      mediaQuery.removeEventListener?.('change', update);
    };
  }, [query]);

  return matches;
};

const useIsDesktopLandingMap = () => useMediaQuery(DESKTOP_LANDING_MAP_QUERY);
const useIsDesktopExplorePanel = () =>
  useMediaQuery(DESKTOP_EXPLORE_PANEL_QUERY);

const MoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M2.5 4.16667C2.5 3.70643 2.8731 3.33333 3.33333 3.33333H17.5C17.9602 3.33333 18.3333 3.70643 18.3333 4.16667C18.3333 4.6269 17.9602 5 17.5 5H3.33333C2.8731 5 2.5 4.6269 2.5 4.16667Z"
      fill="#333333"
    />
    <path
      d="M2.5 10C2.5 9.53976 2.8731 9.16667 3.33333 9.16667H17.5C17.9602 9.16667 18.3333 9.53976 18.3333 10C18.3333 10.4602 17.9602 10.8333 17.5 10.8333H3.33333C2.8731 10.8333 2.5 10.4602 2.5 10Z"
      fill="#333333"
    />
    <path
      d="M2.5 15.8333C2.5 15.3731 2.8731 15 3.33333 15H17.5C17.9602 15 18.3333 15.3731 18.3333 15.8333C18.3333 16.2936 17.9602 16.6667 17.5 16.6667H3.33333C2.8731 16.6667 2.5 16.2936 2.5 15.8333Z"
      fill="#333333"
    />
  </svg>
);

const FilterIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <path
      d="M10.8203 9.07617C12.3541 9.07617 13.6586 10.1116 14.042 11.5303H15.3076C15.729 11.5303 16.0744 11.8756 16.0752 12.2969C16.0752 12.719 15.7297 13.0645 15.3076 13.0645L14.041 13.0654C13.7345 14.5991 12.3919 15.75 10.7812 15.75C9.17067 15.7498 7.82861 14.5991 7.52148 13.0654H2.26758C1.84546 13.0654 1.50002 12.72 1.5 12.2979C1.5 11.8757 1.84545 11.5303 2.26758 11.5303H7.59863C7.98197 10.1116 9.28657 9.07624 10.8203 9.07617ZM10.8203 10.6094C9.82297 10.6095 9.0176 11.4148 9.01758 12.4121C9.01758 13.4095 9.82296 14.2148 10.8203 14.2148C11.7789 14.2148 12.5849 13.4483 12.623 12.4502C12.5849 12.4121 12.584 12.335 12.584 12.2969V12.2197C12.5076 11.3376 11.7406 10.6094 10.8203 10.6094ZM6.75488 2.25C8.36571 2.25 9.70758 3.4007 10.0146 4.93457H15.3076C15.729 4.93457 16.0745 5.27976 16.0752 5.70117C16.0751 6.12324 15.7297 6.46875 15.3076 6.46875H9.97656C9.5933 7.88747 8.28862 8.92368 6.75488 8.92383C5.22101 8.92383 3.91649 7.88756 3.5332 6.46875H2.26758C1.84558 6.46875 1.50021 6.1241 1.5 5.70215C1.5 5.28002 1.84545 4.93457 2.26758 4.93457H3.49512C3.80142 3.40082 5.14424 2.25019 6.75488 2.25ZM6.75488 3.82227C5.75765 3.82247 4.99026 4.5894 4.95215 5.54785C4.99026 5.58596 4.99023 5.66306 4.99023 5.70117V5.77832C5.0666 6.66054 5.83452 7.38867 6.75488 7.38867C7.75208 7.38849 8.55777 6.58339 8.55859 5.625C8.55859 4.62758 7.75231 3.82227 6.75488 3.82227Z"
      fill="#333333"
    />
  </svg>
);

const GeolocateArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.47393 7.18305L2.54948 7.14494L13.8828 2.18664C14.4734 1.92826 15.074 2.52893 14.8157 3.1195L9.85737 14.4528C9.59125 15.0611 8.70973 15.0019 8.52734 14.3635L7.21869 9.78291L2.63881 8.47498C2.02818 8.30051 1.94747 7.48643 2.47393 7.18305Z"
      fill="black"
    />
  </svg>
);

const ZoomInIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M11 3.5V18.5"
      stroke="#1A1A1A"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M3.5 11H18.5"
      stroke="#1A1A1A"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const ZoomOutIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.5 11H18.5"
      stroke="#1A1A1A"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const ExpandAltIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.25 2.625C12.25 2.14175 12.6418 1.75 13.125 1.75H18.375C18.8582 1.75 19.25 2.14175 19.25 2.625V7.875C19.25 8.35825 18.8582 8.75 18.375 8.75C17.8918 8.75 17.5 8.35825 17.5 7.875V4.7375L12.8687 9.36875C12.5269 9.71054 11.9731 9.71054 11.6313 9.36875C11.2895 9.02696 11.2895 8.47304 11.6313 8.13125L16.2625 3.5H13.125C12.6418 3.5 12.25 3.10825 12.25 2.625ZM9.36875 11.6313C9.71054 11.973 9.71054 12.527 9.36875 12.8687L4.7375 17.5H7.875C8.35825 17.5 8.75 17.8918 8.75 18.375C8.75 18.8582 8.35825 19.25 7.875 19.25H2.625C2.14175 19.25 1.75 18.8582 1.75 18.375V13.125C1.75 12.6418 2.14175 12.25 2.625 12.25C3.10825 12.25 3.5 12.6418 3.5 13.125V16.2625L8.13125 11.6313C8.47304 11.2895 9.02696 11.2895 9.36875 11.6313Z"
      fill="#1A1A1A"
    />
  </svg>
);

const LocationPinIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 2C7.85786 2 4.5 5.35786 4.5 9.5C4.5 12.4513 6.31641 15.0855 8.16016 17.0156C9.08496 17.9831 10.0136 18.7705 10.7129 19.3203C11.0625 19.5951 11.3531 19.8094 11.5566 19.9551C11.6584 20.0279 11.7382 20.0837 11.7920 20.1207L11.8531 20.1623L11.8675 20.1721L11.8709 20.1744L11.8717 20.1749C11.9494 20.2266 12.0506 20.2266 12.1283 20.1749L12.1291 20.1744L12.1325 20.1721L12.1469 20.1623L12.2080 20.1207C12.2618 20.0837 12.3416 20.0279 12.4434 19.9551C12.6469 19.8094 12.9375 19.5951 13.2871 19.3203C13.9864 18.7705 14.9150 17.9831 15.8398 17.0156C17.6836 15.0855 19.5 12.4513 19.5 9.5C19.5 5.35786 16.1421 2 12 2ZM12 12C13.3807 12 14.5 10.8807 14.5 9.5C14.5 8.11929 13.3807 7 12 7C10.6193 7 9.5 8.11929 9.5 9.5C9.5 10.8807 10.6193 12 12 12Z"
      fill="#1A1A1A"
    />
  </svg>
);

const MeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1ZM9.99902 18C8.76588 18 7.66339 18.5582 6.92969 19.4355C8.3737 20.4221 10.1192 21 12 21C13.8798 21 15.6238 20.4221 17.0674 19.4365C16.334 18.5589 15.2324 18 13.999 18H9.99902ZM12 3C7.02944 3 3 7.02944 3 12C3 14.3693 3.91655 16.5237 5.41309 18.1309C6.51361 16.8276 8.15966 16 9.99902 16H13.999C15.8388 16 17.4836 16.829 18.584 18.1328C20.0819 16.5254 21 14.3705 21 12C21 7.02944 16.9706 3 12 3ZM12 6C14.7614 6 17 8.23858 17 11C17 13.7614 14.7614 16 12 16C9.23858 16 7 13.7614 7 11C7 8.23858 9.23858 6 12 6ZM12 8C10.3431 8 9 9.34315 9 11C9 12.6569 10.3431 14 12 14C13.6569 14 15 12.6569 15 11C15 9.34315 13.6569 8 12 8Z"
      fill="#333333"
    />
  </svg>
);

/**
 * The new-UI page shell: header (search, filters, language, profile, "..."
 * menu), the hero landing, the explore map with its listing panels, and the
 * flows and dialogs launched from them (menu, feedback, could-buy, add my place).
 */
const NewLandingPage = ({
  selectedBuilding,
  selectBuilding,
  userLocation,
  setLoginModalVisible,
  isLoggedIn,
  hasCheckedLogin,
  userEmail,
  showRemoveListingDialog,
  setShowRemoveListingDialog,
  handleSelectBuildingFromExplore,
  isAdmin,
  countryOfFeedContent,
  setCountryOfFeedContent,
  navViewActive = false,
  onNavigateExplore,
  onOverlayVisibilityChange,
  onRegisterPreviewMetaUpdater,
  onRegisterBuildingLikeAnimationHandler,
  onRegisterAddMyPlaceLauncher,
  onRegisterMapFocusHandler,
  onRegisterShowMapHandler,
  onCloseBuildingView,
  suspendMapWhileBuildingDeepLinked = false,
  listingViewOpen = false,
}: NewLandingPageProps) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const modals = useModals();
  const modalStack = useModalStack();
  // Outcome of a Could-buy from an explore card. This page has no building
  // context to suppress the CTA with, so a refusal has to be shown here.
  const [toastMessage, setToastMessage] = useState<OffMarketToastMessage | string | null>(null);
  const [mapCenter, setMapCenter] = useState<LatLng>(() => userLocation);
  // Search identity stays fixed during small pans; display labels can continue
  // following the viewport independently. The server ranks matches first.
  const [preferredLocation, setPreferredLocation] = useState<PreferredLocation | null>(null);
  const appliedPreferredLocationRef = useRef<PreferredLocation | null>(null);
  const [triggeredFromSearch, setTriggeredFromSearch] = useState(false);
  const [searchResultBounds, setSearchResultBounds] =
    useState<SearchResultBounds | null>(null);
  const [areaLabel, setAreaLabel] = useState<AreaLabel>(EMPTY_AREA_LABEL);
  const { isLoaded: isGoogleMapsLoaded } = useGoogleMapsApi();
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  // Finland-only reverse-geocode fallback caches. Filled lazily as the user
  // pans across postals where Google's reverse-geocode lacks neighborhood data.
  const postalNeighborhoodsCacheRef = useRef<Map<string, string[]>>(new Map());
  const neighborhoodCentroidCacheRef = useRef<Map<string, LatLng>>(new Map());
  // Monotonic id; lets a slow async fallback bail out if a newer moveend
  // started reverse-geocoding before this one's centroid lookups returned.
  const reverseGeocodeAttemptIdRef = useRef(0);
  const [lastMapBoundaryBoxCenter, setLastMapBoundaryBoxCenter] =
    useState<LatLng | null>(null);
  const [lastZoomLevel, setLastZoomLevel] = useState<number | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(isLoggedIn);
  const [playExploreIntro, setPlayExploreIntro] = useState(false);
  const [hasStartedBrowsing, setHasStartedBrowsing] = useState(false);
  const [mapHintLocationRevision, setMapHintLocationRevision] = useState(0);
  const handleSearchMapCenter = useCallback((center: LatLng) => {
    setMapHintLocationRevision((revision) => revision + 1);
    setMapCenter(center);
  }, []);
  const [onboardingMap, setOnboardingMap] = useState<mapboxgl.Map | null>(null);
  // Desktop listing panel collapsed to a compact location+counts card (the
  // Figma `compressed` variant); the list body and postal are hidden. Toggled
  // by the reduce-panel button in the panel header.
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [hasUserInteractedWithMap, setHasUserInteractedWithMap] =
    useState(false);
  const [visibleCounts, setVisibleCounts] = useState<{
    sellers: number;
    buyers: number;
  }>({
    sellers: 0,
    buyers: 0,
  });
  const [isCountsLoading, setIsCountsLoading] = useState(true);
  const [heroContentHeight, setHeroContentHeight] = useState<number | null>(
    null,
  );
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(
    DEFAULT_SEARCH_FILTERS,
  );
  const searchFiltersRef = useRef(searchFilters);
  useLayoutEffect(() => {
    searchFiltersRef.current = searchFilters;
  }, [searchFilters]);

  // JAA-355: after an in-place login, refresh only the conversation badges of
  // the rows already on screen — merged by id, so scroll position, pagination
  // and loading states are untouched and only the badged buttons re-render.
  const resultsForBadgesRef = useRef(results);
  useEffect(() => {
    resultsForBadgesRef.current = results;
  }, [results]);
  // Layout effect so a late listing response never sees a stale false post-commit.
  const isLoggedInRef = useRef(isLoggedIn);
  useLayoutEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);
  /**
   * Fetch the viewer's conversation ids for `listingIds` (default: the rows on
   * screen) and merge them into `results`. Explicit ids let a listing response
   * that was sent logged out but landed after login badge its own rows, since
   * `resultsForBadgesRef` only catches up after the next render.
   */
  const refreshConversationBadges = useCallback(
    async (listingIds?: string[]) => {
      try {
        const map = await fetchConversationIdMap(
          listingIds ?? resultsForBadgesRef.current.map((r) => r._id),
        );
        if (Object.keys(map).length) {
          setResults((prev) => mergeConversationIds(prev, map));
        }
      } catch (error) {
        console.error('Conversation badge refresh failed:', error);
      }
    },
    [],
  );
  useConversationBadgeRefreshOnLogin(isLoggedIn, refreshConversationBadges);
  const [showFilters, setShowFilters] = useState(false);
  const [mapListingFeatureCollection, setMapListingFeatureCollection] =
    useState<OffMarketFeatureCollection | null>(null);
  // True when the last full map refresh left out a building in view (JAA-464).
  const [mapFeaturesTruncated, setMapFeaturesTruncated] = useState(false);
  // The sellers/buyers count pills are the same filter as the Filters panel's
  // listing type — derived from it so the two can never disagree. The marker
  // layers are filtered on could_sell_count / could_buy_count > 0, so to show
  // only one side we zero the opposite count on each feature (its layer then
  // renders nothing). This client-side pass gives instant feedback during the
  // debounced refetch; the server applies the same filter, so once the fresh
  // response lands it is a no-op. The pill counts come from the response and
  // are computed server-side regardless of the listing-type filter, so both
  // stay full while one side is selected.
  const mapFilter: 'all' | 'sellers' | 'buyers' =
    searchFilters.listingType === 'sell'
      ? 'sellers'
      : searchFilters.listingType === 'buy'
        ? 'buyers'
        : 'all';
  const filteredFeatureCollection =
    useMemo<OffMarketFeatureCollection | null>(() => {
      if (!mapListingFeatureCollection) return mapListingFeatureCollection;
      if (mapFilter === 'all') {
        return mapListingFeatureCollection;
      }
      const wantSell = mapFilter === 'sellers';
      const features = mapListingFeatureCollection.features.flatMap((f) => {
        const p = (f.properties ?? {}) as Record<string, number>;
        const keep = wantSell
          ? Number(p.could_sell_count ?? 0) > 0
          : Number(p.could_buy_count ?? 0) > 0;
        if (!keep) return [];
        const properties = wantSell
          ? { ...p, could_buy_count: 0 }
          : { ...p, could_sell_count: 0 };
        return [{ ...f, properties } as typeof f];
      });
      return { ...mapListingFeatureCollection, features };
    }, [mapListingFeatureCollection, mapFilter]);
  // Same sellers/buyers filter applied to the explore panel list.
  const filteredResults = useMemo(() => {
    if (mapFilter === 'sellers') return results.filter((r) => r.could_sell);
    if (mapFilter === 'buyers') return results.filter((r) => r.could_buy);
    return results;
  }, [results, mapFilter]);
  const confirmedResults = useMemo(() =>
    filteredResults.filter((result) => result.match_status !== 'unconfirmed'),
    [filteredResults]);
  const unconfirmedResults = useMemo(() =>
    filteredResults.filter((result) => result.match_status === 'unconfirmed'),
    [filteredResults]);
  const [matchCounts, setMatchCounts] = useState({ confirmed: 0, unconfirmed: 0 });
  // Polygons the map resolves on click/hover, persisted across index rebuilds.
  const geometryCacheRef = useRef<GeometryCache>(new Map());
  // Single source of truth: building_id → { centroid, listings, counts, geometry }.
  // Joins the in-memory feature counts with the listing results so every surface
  // reads the same data instead of re-fetching a (stale) /building-page doc.
  const buildingIndex = useMemo(
    () =>
      buildBuildingIndex(
        results,
        mapListingFeatureCollection,
        geometryCacheRef.current,
      ),
    [results, mapListingFeatureCollection],
  );
  const [pagination, setPagination] = useState<SearchPagination>({
    offset: 0,
    limit: 20,
    total: 0,
    has_more: false,
    next_cursor: null,
  });
  const [isSearchLoading, setIsSearchLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  // Incremented on every fresh (non-append) result set; SearchResults watches it
  // to scroll the list back to the top.
  const [listResetKey, setListResetKey] = useState(0);
  const [showMobileInfoSheet, setShowMobileInfoSheet] = useState(false);

  // On-map building preview — shared service (single source of truth for the
  // active preview, driven by BOTH the map markers and the explore list).
  const preview = useBuildingPreview();
  const visiblePreview = useMemo(
    () => filterPreviewListings(preview.listings, preview.areaDemand, searchFilters),
    [preview.listings, preview.areaDemand, searchFilters],
  );
  const pendingLikeAnimationBuildingRef =
    useRef<BuildingWithConvertedAddresses | null>(null);

  useEffect(() => {
    onRegisterPreviewMetaUpdater?.(preview.updateMeta);
    return () => onRegisterPreviewMetaUpdater?.(null);
  }, [onRegisterPreviewMetaUpdater, preview.updateMeta]);

  // A published interest raises the success toast over the bottom nav, and the
  // preview card sits in front of it. The buyer is done with that building
  // anyway — the same reason the shell closes the building page — so drop the
  // card and leave the toast a clear view.
  const previewClose = preview.close;
  useEffect(() => {
    const onInterestCreated = () => previewClose();
    window.addEventListener('jaakko:buying-interest-created', onInterestCreated);
    return () =>
      window.removeEventListener(
        'jaakko:buying-interest-created',
        onInterestCreated,
      );
  }, [previewClose]);

  const [mobileSheetDragOffset, setMobileSheetDragOffset] = useState(0);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Logged-in users skip the hero and land straight in the explore map (the
  // hero + desktop CTA card are gated to logged-out only). The collapsed
  // map is translated off-screen by --new-landing-hero-content-height, which
  // only the hero sets — so without an expanded map a logged-in session renders
  // blank. Expanding resets that transform to fill the viewport.
  useEffect(() => {
    if (isLoggedIn) setIsMapExpanded(true);
  }, [isLoggedIn]);

  // "Add my place" wizard open state. On completion the real on-map building
  // preview card is opened for the published listing (see handleAddMyPlaceComplete).
  const [addMyPlaceOpen, setAddMyPlaceOpen] = useState(false);
  // "Could buy" wizard: a buyer expressing area interest from a seller listing.
  // Seeded from the clicked SearchResult; null when closed.
  const [couldBuySeed, setCouldBuySeed] = useState<CouldBuySeed | null>(null);
  // The flows stay mounted for their close animation after isOpen turns false.
  // Keep map listing events paused for that full lifetime so body-scroll lock
  // changes cannot reload the result list or seller/buyer counts underneath.
  const [addMyPlaceFlowPresent, setAddMyPlaceFlowPresent] = useState(false);
  const [couldBuyFlowPresent, setCouldBuyFlowPresent] = useState(false);
  // Edit mode (Task 5): pre-fill form from MyAreasView. Set alongside couldBuySeed
  // when the buyer taps Edit on a saved area interest; cleared on close.
  const [couldBuyInitialForm, setCouldBuyInitialForm] = useState<
    CouldBuyFormState | undefined
  >(undefined);
  // The area_interest_id being edited; when set, CouldBuyFlow PUTs instead of POSTs.
  const [couldBuyInterestId, setCouldBuyInterestId] = useState<string | undefined>(undefined);
  // The off-market willingness id being edited (JAA-304): set when the buyer taps
  // Edit on a saved buy listing. Mutually exclusive with couldBuyInterestId — a
  // buy listing is always a single building, never an area interest.
  const [couldBuyWillingnessId, setCouldBuyWillingnessId] = useState<string | undefined>(undefined);
  // When the flow is launched from the building view's "Sell here", this carries
  // the already-known building so the flow opens on the building step pre-filled.
  // Null for the normal address-first entry.
  const [addMyPlaceSeed, setAddMyPlaceSeed] = useState<AddMyPlaceSeed | null>(
    null,
  );
  const searchSlotRefs = useRef<HTMLDivElement[]>([]);
  const registerSearchSlot = useCallback(
    (node: HTMLDivElement | null, index: number) => {
      if (node) {
        searchSlotRefs.current[index] = node;
      } else {
        delete searchSlotRefs.current[index];
      }
    },
    [],
  );
  const handleSearchActivate = useCallback(() => {
    setIsSearchActive(true);
  }, []);
  const handleSearchDeactivate = useCallback(() => {
    setIsSearchActive(false);
  }, []);
  // Sellers/buyers count tabs write the same listingType the Filters panel
  // edits, so toggling either surface drives both. Clicking the active side
  // returns to 'both' (all), matching the tabs' old toggle behavior.
  const handleToggleListingSide = useCallback(
    (side: 'sellers' | 'buyers') => {
      const target = side === 'sellers' ? 'sell' : 'buy';
      setIsSearchLoading(true);
      setSearchFilters((prev) => ({
        ...prev,
        listingType: prev.listingType === target ? 'both' : target,
      }));
    },
    [],
  );
  const heroContentRef = useRef<HTMLDivElement>(null);
  const touchStartPointRef = useRef<{ x: number; y: number } | null>(null);
  const touchMovedRef = useRef(false);
  const mapHandleRef = useRef<BuildingMapHandle>(null);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
  const pendingMapFocusRef = useRef<{
    buildingId: string;
    coords?: LatLng;
  } | null>(null);

  // Buying-interest overlay: once the map is ready, fetch the aggregated demand
  // and draw the stage-coin layer (under the real buy/sell markers),
  // re-anchoring it below the markers as the viewport settles.
  const demandMapRef = useRef<mapboxgl.Map | null>(null);
  // The demand payload, kept so a style reload can redraw the layers without
  // re-fetching it.
  const demandDataRef = useRef<DemandData | null>(null);
  // Assigned by the effect next to focusBuildingOnMap. Held in a ref because the
  // flush is declared far below this callback (it needs the preview service).
  const flushPendingMapFocusRef = useRef<(() => void) | null>(null);
  // Re-fetches the demand overlay and repaints it. Assigned inside handleMapReady,
  // which owns the map instance and the draw closure.
  const reloadDemandRef = useRef<(() => void) | null>(null);
  // mapFilter is derived from listingType, so this single check covers both
  // the Filters panel and the sellers tab.
  const showBuyingInterest = searchFilters.listingType !== 'sell';
  useEffect(() => {
    setDemandVisible(demandMapRef.current, showBuyingInterest);
  }, [showBuyingInterest]);
  const handleMapReady = useCallback((map: mapboxgl.Map) => {
    if (process.env.NODE_ENV !== 'production') {
      (window as unknown as { __landingMap?: mapboxgl.Map }).__landingMap = map;
    }
    // A "View map" that closed a deep-linked building view queued its focus
    // here: the map was suspended at the time, so there was nothing to centre.
    flushPendingMapFocusRef.current?.();
    // BuildingMap unmounts on a desktop/mobile breakpoint flip or when a
    // deep-linked building view suspends the map, and its cleanup calls
    // map.remove() — after which any getSource/getLayer on this instance
    // throws. Drop our reference then, so demand-card hovers in the gap before
    // the replacement map loads are no-ops. onMapReady also re-fires with the
    // SAME map after a style reload, so only wire each instance once.
    const isFirstInstance = demandMapRef.current !== map;
    const settle = () => {
      orderDemandLayers(map);
    };
    const draw = (data: DemandData) => {
      setDemandData(data);
      addDemandLayers(map);
      // A no-op on the first draw; on a reload it repoints the existing source.
      refreshDemandLayers(map);
      settle();
    };

    // A re-fire for a map we already wired: setStyle wiped the sources, so the
    // layers have to be re-added, but the fetch and the viewport listeners must
    // not be duplicated.
    if (!isFirstInstance) {
      if (demandDataRef.current) draw(demandDataRef.current);
      return;
    }

    demandMapRef.current = map;
    setOnboardingMap(map);
    setMapInstance(map);
    const demandRequest = createDemandRequest<DemandData>(
      (filters, signal) => apiRequest('/area-interests/demand', {
        method: 'POST', body: { filters }, signal,
      }),
      (data) => {
        if (demandMapRef.current !== map) return;
        demandDataRef.current = data;
        draw(data);
      },
      (error) => console.error('area-interest demand load failed', error),
    );
    const load = () => demandRequest.load(searchFiltersRef.current);
    map.once('remove', () => {
      demandRequest.dispose();
      if (reloadDemandRef.current === load) reloadDemandRef.current = null;
      setMapInstance(current => current === map ? null : current);
      map.off('moveend', settle);
      map.off('idle', settle);
      if (demandMapRef.current === map) demandMapRef.current = null;
      setOnboardingMap((current) => current === map ? null : current);
    });
    // The overlay is fetched once per map. A buyer who has just published sees
    // their own interest everywhere else immediately, so the map missing its
    // coin until a reload reads as the publish not having worked.
    reloadDemandRef.current = load;
    map.on('moveend', settle);
    map.on('idle', settle);
    load();
  }, []);

  // Repaint the demand overlay when this user publishes an interest, so their
  // own coin appears without a reload.
  useEffect(() => {
    const onCreated = () => reloadDemandRef.current?.();
    window.addEventListener('jaakko:buying-interest-created', onCreated);
    return () =>
      window.removeEventListener('jaakko:buying-interest-created', onCreated);
  }, []);

  const lastDemandFiltersRef = useRef(searchFilters);
  useEffect(() => {
    if (lastDemandFiltersRef.current === searchFilters) return;
    lastDemandFiltersRef.current = searchFilters;
    reloadDemandRef.current?.();
  }, [searchFilters]);

  const menuPanelRef = useRef<HTMLDivElement>(null);
  const mobileSheetContentRef = useRef<HTMLDivElement>(null);
  const geolocateLoadingFallbackRef = useRef<number | null>(null);
  const exploreIntroTimerRef = useRef<number | null>(null);
  const mapListingsRequestRef = useRef<AbortController | null>(null);
  const mapListingsFetchTimerRef = useRef<number | null>(null);
  // A request that started before (or during) the hero-to-map animation may
  // still populate markers and results, but it must not lower their loading
  // states. The settled expanded viewport owns that transition.
  const mapListingsRefreshRevisionRef = useRef(0);
  // Signature of the inputs behind the counts currently on screen, so a refetch
  // that cannot move them (a sellers/buyers toggle) leaves the pills alone.
  const countsSignatureRef = useRef<string | null>(null);

  const playLikeAnimation = useCallback(
    (building: BuildingWithConvertedAddresses) => {
      mapHandleRef.current?.playLikeAnimation(building);
    },
    [],
  );

  const handleBuildingLikeAnimation = useCallback(
    (building: BuildingWithConvertedAddresses) => {
      if (selectedBuilding) {
        pendingLikeAnimationBuildingRef.current = building;
        return;
      }
      playLikeAnimation(building);
    },
    [playLikeAnimation, selectedBuilding],
  );

  useEffect(() => {
    onRegisterBuildingLikeAnimationHandler?.(handleBuildingLikeAnimation);
    return () => onRegisterBuildingLikeAnimationHandler?.(null);
  }, [handleBuildingLikeAnimation, onRegisterBuildingLikeAnimationHandler]);

  useEffect(() => {
    if (selectedBuilding) return undefined;
    const building = pendingLikeAnimationBuildingRef.current;
    if (!building) return undefined;
    pendingLikeAnimationBuildingRef.current = null;
    const handle = window.setTimeout(() => playLikeAnimation(building), 250);
    return () => window.clearTimeout(handle);
  }, [playLikeAnimation, selectedBuilding]);
  const languageIsFinnish = i18n.language.startsWith('fi');
  const isDesktopLandingMap = useIsDesktopLandingMap();
  // Desktop (panel visible) drills the left panel into the building; mobile
  // (no left panel) keeps the floating docked preview card.
  const isDesktopExplorePanel = useIsDesktopExplorePanel();
  // The full building view sits over the map (desktop starts below the header),
  // so the header search + filters and the explore controls must be hidden while
  // it is open. selectedBuilding is set only for the full view, not the on-map
  // preview, so it is the right signal.
  const isBuildingViewOpen = selectedBuilding !== null;
  const isListingActionFlowPresent =
    addMyPlaceOpen ||
    Boolean(couldBuySeed) ||
    addMyPlaceFlowPresent ||
    couldBuyFlowPresent;
  const handleFiltersChange = useCallback((updates: Partial<SearchFilters>) => {
    setSearchFilters((prev) => mergeAppliedFilters(prev, updates));
  }, []);
  const isListingActionFlowPresentRef = useRef(isListingActionFlowPresent);
  useLayoutEffect(() => {
    isListingActionFlowPresentRef.current = isListingActionFlowPresent;
  }, [isListingActionFlowPresent]);
  const {
    deferIfBlocked: deferMapListingsSearchWhileFlowPresent,
    releaseRevision: deferredMapListingsSearchRevision,
  } = useDeferredMapListingsRefresh(isListingActionFlowPresent);
  const shouldSuspendLandingMap =
    suspendMapWhileBuildingDeepLinked && isBuildingViewOpen;
  const hideHeaderMapControls =
    navViewActive || isBuildingViewOpen || listingViewOpen;
  // The listings sheet stays open under a nav view (a chat opened from one of its cards) so closing that view returns to it; it only counts as on screen when nothing covers it and the layout is not desktop, where it is display:none (JAA-444).
  const infoSheetOnScreen =
    showMobileInfoSheet && !navViewActive && !isDesktopExplorePanel;
  const listingViewport = useMapListingViewport(
    mapInstance, mapBounds,
    isSearchActive || showFilters || navViewActive ||
      isBuildingViewOpen || listingViewOpen || showMobileMenu || showFeedbackModal ||
      isListingActionFlowPresent || playExploreIntro,
    `${isLoggedIn}:${isMapExpanded}:${isDesktopExplorePanel}:${i18n.language}`,
    showMobileInfoSheet,
  );
  const visibleBounds = listingViewport?.bounds ?? null;
  const TOUCH_MOVE_THRESHOLD = 8;
  const noFiltersSet =
    searchFilters.location === null &&
    searchFilters.listingType === 'both' &&
    searchFilters.priceRange.min === null &&
    searchFilters.priceRange.max === null &&
    searchFilters.size.min === null &&
    searchFilters.size.max === null &&
    searchFilters.rooms === null &&
    searchFilters.floor.min === null &&
    searchFilters.floor.max === null &&
    searchFilters.renovationStatus === null &&
    searchFilters.hasElevator === null &&
    searchFilters.hasBalcony === null;

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (searchFilters.listingType !== 'both') count++;
    if (searchFilters.size.min !== null || searchFilters.size.max !== null)
      count++;
    if (searchFilters.rooms !== null) count++;
    if (searchFilters.floor.min !== null || searchFilters.floor.max !== null)
      count++;
    if (searchFilters.renovationStatus !== null) count++;
    if (searchFilters.hasElevator === true) count++;
    if (searchFilters.hasBalcony === true) count++;
    return count;
  }, [searchFilters]);

  const handleToggleLanguage = () => {
    const nextLanguage = languageIsFinnish ? 'en' : 'fi';
    i18n.changeLanguage(nextLanguage);
    localStore.setItem('i18nextLng', nextLanguage);
  };

  const clearExploreIntroTimer = useCallback(() => {
    if (exploreIntroTimerRef.current === null) return;
    window.clearTimeout(exploreIntroTimerRef.current);
    exploreIntroTimerRef.current = null;
  }, []);

  const triggerExploreIntro = useCallback(() => {
    clearExploreIntroTimer();
    // The collapsed map may still have a debounced refresh queued. If it starts
    // after expansion, it looks current but describes the old overlay layout
    // and can make the count pill finish loading before the animation settles.
    if (mapListingsFetchTimerRef.current !== null) {
      window.clearTimeout(mapListingsFetchTimerRef.current);
      mapListingsFetchTimerRef.current = null;
    }
    mapListingsRefreshRevisionRef.current += 1;
    setIsSearchLoading(true);
    setIsCountsLoading(true);
    setPlayExploreIntro(true);
    exploreIntroTimerRef.current = window.setTimeout(() => {
      exploreIntroTimerRef.current = null;
      // Invalidate anything that was started by a map event during the intro.
      // The viewport hook resumes below and starts one request from the final
      // control geometry.
      mapListingsRefreshRevisionRef.current += 1;
      setPlayExploreIntro(false);
    }, EXPLORE_INTRO_ANIMATION_MS);
  }, [clearExploreIntroTimer]);

  const handleExpandMap = useCallback(() => {
    // Any deliberate expansion from the landing hero starts the lesson. Login,
    // deep links, and other programmatic paths call setIsMapExpanded directly.
    setHasStartedBrowsing(true);
    if (!isMapExpanded) {
      triggerExploreIntro();
    }
    setIsMapExpanded(true);
  }, [isMapExpanded, triggerExploreIntro]);

  // The CTA shares the same expansion path as tapping, swiping, or scrolling
  // the preview map, so every deliberate hero interaction arms onboarding.
  const handleStartBrowsing = useCallback(() => {
    handleExpandMap();
  }, [handleExpandMap]);

  // Null seed opens the flow unseeded (address step first) — the Tools flows'
  // "Add my place" CTA uses this; "Sell here" passes a real seed.
  const openAddMyPlaceWithSeed = useCallback((seed: AddMyPlaceSeed | null) => {
    preview.selectionRef.current += 1;
    setAddMyPlaceSeed(seed);
    setAddMyPlaceOpen(true);
  }, [preview.selectionRef]);

  const handleCloseAddMyPlace = useCallback(() => {
    setAddMyPlaceOpen(false);
    setAddMyPlaceSeed(null);
  }, []);

  // Expose the seeded launcher so AppContent can wire it into the building
  // view's "Sell here" (mirrors onRegisterBuildingLikeAnimationHandler).
  useEffect(() => {
    onRegisterAddMyPlaceLauncher?.(openAddMyPlaceWithSeed);
    return () => onRegisterAddMyPlaceLauncher?.(null);
  }, [onRegisterAddMyPlaceLauncher, openAddMyPlaceWithSeed]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const isAmpPaymentReturn = url.searchParams.has(AMP_PAYMENT_QUERY_PARAM);
    const hasAmpRestoreState =
      localStore.getItem(AMP_RESTORE_STORAGE_KEY) !== null;

    if (!isAmpPaymentReturn) return;

    if (hasAmpRestoreState) {
      setAddMyPlaceOpen(true);
    }

    url.searchParams.delete(AMP_PAYMENT_QUERY_PARAM);
    window.history.replaceState(
      {},
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);

  const clearGeolocateLoadingFallback = useCallback(() => {
    if (geolocateLoadingFallbackRef.current === null) return;
    window.clearTimeout(geolocateLoadingFallbackRef.current);
    geolocateLoadingFallbackRef.current = null;
  }, []);

  const handleGeolocateStart = useCallback(() => {
    setPreferredLocation(null);
    clearGeolocateLoadingFallback();
    setIsGeolocating(true);
    geolocateLoadingFallbackRef.current = window.setTimeout(() => {
      geolocateLoadingFallbackRef.current = null;
      setIsGeolocating(false);
    }, GEOLOCATE_LOADING_FALLBACK_MS);
  }, [clearGeolocateLoadingFallback]);

  const handleGeolocateEnd = useCallback(() => {
    clearGeolocateLoadingFallback();
    setIsGeolocating(false);
  }, [clearGeolocateLoadingFallback]);

  const handleGeolocate = () => {
    if (isGeolocating) return;
    mapHandleRef.current?.triggerGeolocate();
  };

  const handleZoomIn = () => {
    mapHandleRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    mapHandleRef.current?.zoomOut();
  };

  const handleMapUserInteraction = useCallback(() => {
    setHasUserInteractedWithMap(true);
  }, []);

  const handleMapListingsMoveStart = useCallback(() => {
    if (isListingActionFlowPresentRef.current) return;
    setIsSearchLoading(true);
    setIsCountsLoading(true);
  }, []);

  // The map reports a movement that moved nothing — a container resize, which
  // on a phone is every soft keyboard. movestart already raised the skeletons,
  // and the fetch that would clear them is deliberately not happening, so they
  // have to be lowered here or the list stays loading forever. Only when
  // nothing is genuinely in flight: a real fetch still owns its own spinner.
  const handleMapListingsMoveDiscarded = useCallback(() => {
    if (isListingActionFlowPresentRef.current) return;

    // A different place can resolve to the same camera bounds. Its ranking
    // still needs a fresh first page even though the map did not move.
    if (visibleBounds && appliedPreferredLocationRef.current !== preferredLocation) {
      performMapListingsSearchRef.current(visibleBounds, false);
      return;
    }
    if (mapListingsRequestRef.current) return;
    setIsSearchLoading(false);
    setIsCountsLoading(false);
  }, [visibleBounds, preferredLocation]);

  // Global home. From a building view this lands on the same map as "View map"
  // for a logged-in user, but the two are not the same action: this one drops
  // the building context (the view AND its on-map preview) and leaves the camera
  // and filters exactly as the user left them, while "View map" is a contextual
  // return that keeps the building centred and selected. Signed in or out it
  // also leaves every nav view, so a guest's chat opened with Ask seller closes
  // instead of staying on top of the page it navigates (JAA-444).
  const handleLogoClick = () => {
    onCloseBuildingView?.();
    preview.close();
    // Dropping the building context is the whole point of this action, so a
    // focus queued by an earlier "View map" must not fire on the map this
    // click is about to mount and pull the user back to the building.
    pendingMapFocusRef.current = null;
    setHasUserInteractedWithMap(false);
    setShowMobileMenu(false);
    setShowFilters(false);
    setShowMobileInfoSheet(false);
    onNavigateExplore();

    if (isLoggedIn) {
      setIsMapExpanded(true);
      return;
    }

    // Logged out, home is the landing page itself: someone who arrived on a
    // shared building link may not know what Jaakko is, so the copy is the
    // orientation they are missing.
    setIsMapExpanded(false);
    clearExploreIntroTimer();
    setPlayExploreIntro(false);
    // onNavigateExplore already pushed '/' when off the root; push only to drop a query left on '/' (a shared building link).
    if (history.location.pathname !== '/' || history.location.search) {
      history.push('/');
    }
  };

  const showExploreMap = useCallback((center?: LatLng) => {
    preview.close();
    pendingMapFocusRef.current = null;
    setShowMobileMenu(false);
    setShowFilters(false);
    setShowMobileInfoSheet(false);
    setIsSearchActive(false);
    setIsMapExpanded(true);
    if (
      center &&
      Number.isFinite(center.lat) &&
      Number.isFinite(center.lng) &&
      center.lat >= -90 &&
      center.lat <= 90 &&
      center.lng >= -180 &&
      center.lng <= 180
    ) {
      // Updating the prop covers a map that is still mounting; the imperative
      // fly covers the already-live map behind the listing modal.
      setMapCenter(center);
      mapHandleRef.current?.flyTo(center, AREA_INTEREST_MAP_ZOOM);
    }
    onNavigateExplore();
  }, [onNavigateExplore, preview]);

  useEffect(() => {
    onRegisterShowMapHandler?.(showExploreMap);
    return () => onRegisterShowMapHandler?.(null);
  }, [onRegisterShowMapHandler, showExploreMap]);

  const handleOpenProfile = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'open_profile_view_landing_page',
        category: 'User Interaction',
        action: 'Click',
        label: 'Open Profile View Landing Page',
      },
    });
    history.push('/profile', createProfileLocationState(history.location));
  };

  /**
   * Open the active preview's building — a new tab on desktop, the in-place
   * building view on mobile. The preview stays open in both cases.
   */
  const expandPreview = useCallback(() => {
    const building = preview.building;
    if (!building) return;
    preview.selectionRef.current += 1;
    if (isDesktopExplorePanel) {
      openBuildingInNewTab(building.id, building.addresses?.[0]?.coordinates);
      return;
    }
    selectBuilding(building);
  }, [preview, selectBuilding, isDesktopExplorePanel, openBuildingInNewTab]);

  const handlePreviewToggleLike = useCallback(() => {
    preview.toggleLike(playLikeAnimation);
  }, [playLikeAnimation, preview]);

  // Keep the building visually selected on the map (green polygon + selected
  // sell icon + dot) for as long as its preview is open.
  useEffect(() => {
    mapHandleRef.current?.highlightBuilding(preview.building?.id ?? null);
  }, [preview.building]);

  // The area's first load already holds this building's listings (in `results`)
  // and its buyer/like counts (in the aggregated map features). Seed the preview
  // from that in-memory data so it paints instantly instead of waiting on a
  // re-fetch; the network call then refreshes followers/liked in the background.
  const buildPreviewSeed = useCallback(
    (buildingId: string): BuildingPreviewSeed | undefined => {
      const entry = buildingIndex.get(buildingId);
      if (!entry || entry.listings.length === 0) return undefined;
      return {
        listings: entry.listings as unknown as OffMarketWillingnessEvent[],
        meta: {
          buyers: entry.counts.buyers,
          likes: entry.counts.likes,
          followers: 0,
          liked: false,
        },
      };
    },
    [buildingIndex],
  );

  // Open from the explore list: resolve the building from the in-memory index
  // (centroid + listings + any map-resolved polygon) and open the view directly,
  // skipping the /building-page re-fetch that returns a stale, centroid-less doc.
  // Falls back to the parent fetch only on an index miss (e.g. deep-link / out of
  // the loaded area), so nothing regresses for buildings we don't have cached.
  const handleExploreSelect = useCallback(
    (buildingId: string) => {
      preview.selectionRef.current += 1;
      const entry = buildingIndex.get(buildingId);
      if (entry) {
        selectBuilding(buildingFromIndexEntry(entry, geometryCacheRef.current));
        return;
      }
      handleSelectBuildingFromExplore(buildingId);
    },
    [buildingIndex, selectBuilding, handleSelectBuildingFromExplore, preview.selectionRef],
  );

  /**
   * Explore-list card click, desktop and mobile alike — open that one listing
   * in the single-listing view (JAA-345). It layers over this still-mounted
   * pane, so map, search and scroll survive and closing returns here.
   */
  const openListingById = useOpenListing();
  const openListing = useCallback(
    (listing: Parameters<typeof openListingById>[0]) => {
      preview.selectionRef.current += 1;
      openListingById(listing);
    },
    [openListingById, preview],
  );

  /**
   * "Could buy" pill on an explore seller card: opens a draft chat with that
   * seller. The click writes nothing; the buyer's first message creates the
   * conversation. Signed-out viewers get the same draft and log in at Send
   * (JAA-325). The listings sheet stays open under the chat, so closing the
   * chat returns to the list the card was in (JAA-444).
   */
  const handleCouldBuyFromCard = useCallback(
    async (result: SearchResult) => {
      // One intent per building: refuse before the chat opens rather than letting
      // the send fail, which would mean typing a message first to be told no.
      // The loaded results contain the viewer's own listings, so the answer is
      // already here; the backend enforces the same rule for anything this
      // client-side view cannot see.
      const sellsHere = results.some(
        (r) => r.building_id === result.building_id && r.isUserOwnEvent && r.could_sell,
      );
      if (sellsHere) {
        setToastMessage('USER_SELLER_AND_BUYER');
        return;
      }
      // Opens the chat and creates nothing. This used to POST a building-level
      // could-buy first, so messaging one seller registered the buyer as demand
      // for their whole building; the conversation is created by the first
      // message instead.
      window.dispatchEvent(
        new CustomEvent('jaakko:open-draft-chat', {
          detail: {
            sellWillingnessId: result._id,
            title: `${result.address.street} ${result.address.houseNumber}, ${result.address.city}`,
          },
        }),
      );
    },
    [results],
  );

  /**
   * Explore-list "Could buy" pill — open the CouldBuyFlow seeded from the
   * clicked seller listing (the buyer expresses area interest, JAA-278).
   */
  const handleCouldBuy = useCallback((result: SearchResult) => {
    setCouldBuyInitialForm(undefined);
    setCouldBuyInterestId(undefined);
    setCouldBuyWillingnessId(undefined);
    setCouldBuySeed({
      buildingId: result.building_id,
      coordinates: result.address.coordinates ?? null,
      address: {
        street: result.address.street,
        houseNumber: result.address.houseNumber,
        city: result.address.city,
        country: result.address.country,
      },
    });
  }, []);

  // "Open chat" on a listing card (JAA-355) opens the Messages modal, which
  // keeps the app-header strip visible above it. With the mobile listings sheet
  // open that strip holds the explore controls instead of the header, so they
  // would show through; leave the sheet, as opening a chat leaves the list.
  useEffect(() => {
    const handleOpenConversation = () => {
      setShowMobileInfoSheet(false);
      setShowFilters(false);
      setIsSearchActive(false);
    };
    window.addEventListener('jaakko:open-conversation', handleOpenConversation);
    return () => window.removeEventListener('jaakko:open-conversation', handleOpenConversation);
  }, []);

  /**
   * Edit an existing area interest from MyAreasView (Task 5). Opens CouldBuyFlow
   * with a synthetic seed (no coordinates — anchor building is stored server-side)
   * and pre-fills the form from the stored display_areas/criteria/notify values.
   * ProfileView dispatches the custom 'jaakko:edit-area-interest' event because it
   * lives in the modal stack above this component.
   */
  useEffect(() => {
    const handleEditAreaInterest = (e: Event) => {
      const { interest } = (e as CustomEvent).detail as { interest: AreaInterest };
      const prefillForm = formFromAreaInterest(interest);
      setCouldBuyInitialForm(prefillForm);
      setCouldBuyInterestId(interest.area_interest_id);
      setCouldBuyWillingnessId(undefined);
      // Seed from what the interest already carries. It used to be seeded blank,
      // which left the flow with no address to label the building chip with and
      // no centre for the map, so re-opening an interest to edit it showed no
      // location at all (JAA-328).
      const addr = interest.anchor_address;
      setCouldBuySeed({
        buildingId: interest.anchor_building_id ?? '',
        coordinates: prefillForm.circleCentre,
        address: {
          street: addr?.street ?? '',
          houseNumber: addr?.houseNumber ?? '',
          city: addr?.city ?? '',
          country: '',
        },
      });
    };
    window.addEventListener(
      'jaakko:edit-area-interest',
      handleEditAreaInterest,
    );
    return () =>
      window.removeEventListener(
        'jaakko:edit-area-interest',
        handleEditAreaInterest,
      );
  }, []);

  /**
   * Edit an existing buy listing (JAA-304). Opens CouldBuyFlow pre-filled from
   * the listing's own description/size/rooms (mapped by listingToCouldBuyForm at
   * the dispatch site) with the full seed already resolved — unlike the area-
   * interest edit above, a buy listing's building/address is known client-side,
   * so no synthetic blank seed is needed here. Both MyListings (own buy cards)
   * and the building manage menu (useListingManagement) dispatch this event
   * because neither has a prop path down to this CouldBuyFlow instance.
   */
  useEffect(() => {
    const handleEditBuyListing = (e: Event) => {
      const { seed, prefillForm, willingnessId } = (e as CustomEvent).detail as {
        seed: CouldBuySeed;
        prefillForm: CouldBuyFormState;
        willingnessId: string;
      };
      setCouldBuyInitialForm(prefillForm);
      setCouldBuyInterestId(undefined);
      setCouldBuyWillingnessId(willingnessId);
      setCouldBuySeed(seed);
    };
    window.addEventListener('jaakko:edit-buy-listing', handleEditBuyListing);
    return () => window.removeEventListener('jaakko:edit-buy-listing', handleEditBuyListing);
  }, []);

  /**
   * Building "Buy here" (JAA-278). The building modal (OffMarketBuyAndSell /
   * BuildingView via useOffMarketIntent) dispatches 'jaakko:open-could-buy' with
   * the anchor building, since it has no prop path down to this CouldBuyFlow.
   * Open the area flow seeded from it — works for empty buildings too, the seed
   * carries the building's coordinates so the 500 m circle still centres on it.
   */
  useEffect(() => {
    const handleOpenCouldBuy = (e: Event) => {
      preview.selectionRef.current += 1;
      const { buildingId, coordinates, address } = (e as CustomEvent)
        .detail as CouldBuySeed;
      setCouldBuyInitialForm(undefined);
      setCouldBuyInterestId(undefined);
      setCouldBuyWillingnessId(undefined);
      setCouldBuySeed({
        buildingId,
        coordinates: coordinates ?? null,
        address: {
          street: address?.street ?? '',
          houseNumber: address?.houseNumber ?? '',
          city: address?.city ?? '',
          country: address?.country ?? '',
        },
      });
    };
    window.addEventListener('jaakko:open-could-buy', handleOpenCouldBuy);
    return () =>
      window.removeEventListener('jaakko:open-could-buy', handleOpenCouldBuy);
  }, [preview.selectionRef]);

  /**
   * Activity FAB "Could sell" (JAA-284). The ActivityView FAB dispatches
   * 'jaakko:open-add-my-place' so it can trigger AddMyPlaceFlow without a
   * direct prop path from AppContent down here.
   */
  useEffect(() => {
    const handleOpenAddMyPlace = () => setAddMyPlaceOpen(true);
    window.addEventListener('jaakko:open-add-my-place', handleOpenAddMyPlace);
    return () =>
      window.removeEventListener(
        'jaakko:open-add-my-place',
        handleOpenAddMyPlace,
      );
  }, []);

  /**
   * Listing page "Could sell" (buyer-listing prompt). The listing modal
   * dispatches 'jaakko:open-could-sell' with its building + address, since it
   * has no prop path down to this AMP flow (same pattern as
   * 'jaakko:open-could-buy' above). Builds an AddMyPlaceSeed mirroring
   * BuildingView.buildSellSeed; geometry stays null — the AMP publish path
   * re-resolves it (BuildingPreviewCard precedent). Without coordinates a seed
   * is impossible (center is required), so fall back to the unseeded flow.
   */
  useEffect(() => {
    const handleOpenCouldSell = (e: Event) => {
      const { buildingId, coordinates, address } = (e as CustomEvent).detail as {
        buildingId: string;
        coordinates: { lat: number; lng: number } | null;
        address: {
          street?: string;
          houseNumber?: string;
          city?: string;
          country?: string;
          postcode?: string;
        };
      };
      if (!coordinates) {
        setAddMyPlaceSeed(null);
        setAddMyPlaceOpen(true);
        return;
      }
      openAddMyPlaceWithSeed({
        buildingId,
        address: [address?.street, address?.houseNumber]
          .filter(Boolean)
          .join(' ')
          .trim(),
        area: address?.city ?? '',
        postal: address?.postcode ?? '',
        center: coordinates,
        addressObject: {
          country: address?.country ?? '',
          region: '',
          city: address?.city ?? '',
          postcode: address?.postcode ?? '',
          street: address?.street ?? '',
          houseNumber: address?.houseNumber ?? '',
          coordinates,
        },
        geometry: null,
      });
    };
    window.addEventListener('jaakko:open-could-sell', handleOpenCouldSell);
    return () => window.removeEventListener('jaakko:open-could-sell', handleOpenCouldSell);
  }, [openAddMyPlaceWithSeed]);

  /**
   * Explore-list card hover (desktop master pane) — highlight the hovered
   * building's marker, falling back to the open preview's building on hover-out.
   */
  const handleHoverListing = useCallback(
    (buildingId: string | null) => {
      mapHandleRef.current?.highlightBuilding(
        buildingId ?? preview.building?.id ?? null,
      );
    },
    [preview.building],
  );

  /**
   * Map building selection: opens the on-map preview for a new building, or
   * swaps in the enriched object when it is the one already open. Listing
   * markers fire twice per click (bare open with addresses: [], then enrich
   * once the reverse geocode resolves); bare buildings fire once, and only once
   * the geocode names them (the first address, or the district when there is
   * none). `selectionToken` is the click's value of the preview's
   * selectionRef; once a newer click, a Close, an outside open or an unmount
   * has advanced it, the callback is stale and does nothing.
   */
  const handleBuildingSelect = (
    building: BuildingWithConvertedAddresses,
    point?: { x: number; y: number },
    selectionToken?: number,
  ) => {
    if (selectionToken !== preview.selectionRef.current) return;
    // Phase 2: the map assembles a real footprint polygon when a building is
    // clicked. Cache it by id so a later explore-list open of the same building
    // gets the outline (not just a centred point). Empty placeholder geometry
    // (coordinates: []) is ignored. Only Polygon is cached — the GeometryCache
    // shape is Polygon-only, so coercing a MultiPolygon here would store invalid
    // GeoJSON and reopen the building with a broken outline.
    const coords = building.geometry?.coordinates;
    if (
      building.geometry?.type === 'Polygon' &&
      Array.isArray(coords) &&
      coords.length > 0
    ) {
      geometryCacheRef.current.set(building.id, {
        type: 'Polygon',
        coordinates: coords,
      });
    }

    if (isMapExpanded || isDesktopExplorePanel) {
      preview.openFromMap(building, point, buildPreviewSeed(building.id));
      return;
    }

    handleExpandMap();
  };

  const resetMapTouchGesture = () => {
    touchStartPointRef.current = null;
    touchMovedRef.current = false;
  };

  const handleMapTouchStart = (event: React.TouchEvent<HTMLButtonElement>) => {
    const touch = event.touches[0];
    if (!touch) return;

    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchMovedRef.current = false;
  };

  const handleMapTouchMove = (event: React.TouchEvent<HTMLButtonElement>) => {
    const touch = event.touches[0];
    const startPoint = touchStartPointRef.current;
    if (!touch || !startPoint) return;

    if (
      Math.abs(touch.clientX - startPoint.x) > TOUCH_MOVE_THRESHOLD ||
      Math.abs(touch.clientY - startPoint.y) > TOUCH_MOVE_THRESHOLD
    ) {
      touchMovedRef.current = true;
    }
  };

  const handleMapTouchEnd = () => {
    const shouldExpandAfterDrag = touchMovedRef.current;
    resetMapTouchGesture();

    if (shouldExpandAfterDrag) {
      handleExpandMap();
    }
  };

  const handleMapTouchCancel = () => {
    resetMapTouchGesture();
  };

  const paginationRef = useRef<SearchPagination>(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  /**
   * Fetch map listings for `bounds` (a fresh page, or the next page when
   * `loadMore`) and apply rows, markers, the zoom-in hint, pagination and
   * counts. A request sent logged out that lands after an in-place login
   * re-badges its own rows, so the late anonymous response cannot wipe the
   * "Open chat" labels.
   */
  const performMapListingsSearch = useCallback(
    async (bounds: MapBounds, loadMore: boolean = false) => {
      const refreshRevision = mapListingsRefreshRevisionRef.current;
      mapListingsRequestRef.current?.abort();
      const controller = new AbortController();
      mapListingsRequestRef.current = controller;
      const sentLoggedIn = isLoggedInRef.current;

      const signature = JSON.stringify([countsSignature(bounds, searchFilters), listingViewport?.geometry]);
      if (!loadMore) {
        setIsSearchLoading(true);
        if (signature !== countsSignatureRef.current) {
          setIsCountsLoading(true);
        }
      } else {
        setIsLoadingMore(true);
      }

      try {
        const requestBody: Record<string, unknown> = {
          bounds,
          visibleGeometry: listingViewport?.geometry,
          preferredLocation: preferredLocationInView(preferredLocation, bounds),
          filters: searchFilters,
          listingType: searchFilters.listingType,
          limit: MAP_LISTINGS_PAGE_LIMIT,
        };

        if (loadMore) {
          const cursor = paginationRef.current.next_cursor;
          if (!cursor) {
            setIsLoadingMore(false);
            return;
          }
          requestBody.cursor = cursor;
        }

        const { data, restarted } = await fetchMapListingsPage(
          requestBody, getCookie('csrf_access_token') || '', controller.signal,
        );
        const appendResults = loadMore && !restarted;
        if (mapListingsRequestRef.current !== controller) {
          return;
        }
        const incomingResults: SearchResult[] = data.results || [];
        const nextCursor = data.pagination?.next_cursor || null;
        const hasMore = Boolean(data.pagination?.has_more);
        if (!appendResults) {
          setMatchCounts(data.matchCounts ?? { confirmed: data.total ?? 0, unconfirmed: 0 });
        }

        if (appendResults) {
          setResults((prev) => [...prev, ...incomingResults]);
        } else {
          appliedPreferredLocationRef.current = preferredLocation;
          setResults(incomingResults);
          // Fresh viewport: send the list back to the top. Replacing `results`
          // alone leaves the viewer scrolled to where page 4 used to be, which
          // reads as the list never having reset. Covers address search too —
          // that flies the map, so it settles through this same path.
          setListResetKey((k) => k + 1);
          // Before the collection lands: the map stamps is_seen from this set
          // as it ingests the features, so adopting it afterwards would flash
          // yellow on markers the viewer has already clicked.
          setServerSeenBuildings(data.seenBuildingIds);
          // JAA-253: per-listing features come from /off-market-listings-in-bounds;
          // collapse to one Feature per building client-side so marker counts can
          // react to FE-only state (filters, favorites) without a round-trip.
          setMapListingFeatureCollection(
            aggregateBuildingFeatures(data.mapFeatures),
          );
          setMapFeaturesTruncated(data.mapFeaturesTruncated === true);
        }
        if (!sentLoggedIn && isLoggedInRef.current) {
          refreshConversationBadges(incomingResults.map((r) => r._id));
        }

        setPagination((previous) => ({
          offset: 0,
          limit: data.pagination?.limit ?? MAP_LISTINGS_PAGE_LIMIT,
          total: appendResults ? previous.total : (data.total ?? 0),
          has_more: hasMore,
          next_cursor: nextCursor,
        }));

        if (!appendResults) {
          const incomingCounts = data.visibleCounts;
          setVisibleCounts({
            sellers: Number.isFinite(incomingCounts?.sellers)
              ? incomingCounts.sellers
              : 0,
            buyers: Number.isFinite(incomingCounts?.buyers)
              ? incomingCounts.buyers
              : 0,
          });
          countsSignatureRef.current = signature;
          if (refreshRevision === mapListingsRefreshRevisionRef.current) {
            setIsCountsLoading(false);
          }
        }
      } catch (error) {
        if ((error as { name?: string })?.name === 'AbortError') {
          return;
        }
        console.error('Map listings search error:', error);
        if (
          !loadMore &&
          refreshRevision === mapListingsRefreshRevisionRef.current
        ) {
          setIsCountsLoading(false);
        }
      } finally {
        if (mapListingsRequestRef.current === controller) {
          mapListingsRequestRef.current = null;
          if (refreshRevision === mapListingsRefreshRevisionRef.current) {
            setIsSearchLoading(false);
          }
          setIsLoadingMore(false);
        }
      }
    },
    [searchFilters, preferredLocation, refreshConversationBadges, listingViewport],
  );

  // Latest search fn for the viewport effect below, which must not re-run just
  // because this callback was rebuilt.
  const performMapListingsSearchRef = useRef(performMapListingsSearch);
  useEffect(() => {
    performMapListingsSearchRef.current = performMapListingsSearch;
  }, [performMapListingsSearch]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !pagination.has_more || !visibleBounds) return;
    performMapListingsSearch(visibleBounds, true);
  }, [
    isLoadingMore,
    pagination.has_more,
    visibleBounds,
    performMapListingsSearch,
  ]);

  /** Drops a removed listing from the explore results and its count. */
  const handleRemoveResult = useCallback((listingId: string) => {
    const removed = resultsForBadgesRef.current.find(
      (result) => result._id === listingId,
    );
    if (!removed) return;
    resultsForBadgesRef.current = resultsForBadgesRef.current.filter(
      (result) => result._id !== listingId,
    );
    setResults((prev) => prev.filter((result) => result._id !== listingId));
    setPagination((prev) => ({
      ...prev,
      total: Math.max(0, prev.total - 1),
    }));
    setMatchCounts((prev) => ({
      ...prev,
      [removed.match_status === 'unconfirmed' ? 'unconfirmed' : 'confirmed']:
        Math.max(0, prev[removed.match_status === 'unconfirmed' ? 'unconfirmed' : 'confirmed'] - 1),
    }));
  }, []);

  const handleVisibleBoundsChange = useCallback((bounds: MapBounds) => {
    if (isListingActionFlowPresentRef.current) return;
    setMapBounds(bounds);
    setPreferredLocation((place) => preferredLocationInView(place, bounds));
  }, []);

  const handleOpenMobileInfoSheet = () => {
    setMobileSheetDragOffset(0);
    setShowMobileInfoSheet(true);
  };

  const handleCloseMobileInfoSheet = () => {
    setMobileSheetDragOffset(0);
    setShowMobileInfoSheet(false);
  };

  const performSearch = useCallback(
    (_loadMore?: boolean) => {
      if (!visibleBounds) return;
      performMapListingsSearch(visibleBounds, false);
    },
    [visibleBounds, performMapListingsSearch],
  );

  // Open the on-map building preview by id: land in the expanded-map state,
  // fetch the full building, centre the map on it, and anchor the preview to it
  // ON THE MAP. Shared by the explore-list "View on map" and the post-AMP
  // completion so both reuse the exact same listing-on-map behaviour.
  const openBuildingPreviewById = useCallback(
    (buildingId: string, coords?: LatLng) => {
      // User intent starts here, before either the fetch or map anchoring settles.
      const selectionToken = ++preview.selectionRef.current;
      setIsMapExpanded(true);
      fetch(
        `${process.env.REACT_APP_API_URL}/building-page?building-id=${buildingId}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
          },
        },
      )
        .then((res) => res.json())
        .then((payload) => {
          if (selectionToken !== preview.selectionRef.current) return;
          // building-page wraps the building in { data: {...} }
          const building = (payload?.data ||
            payload) as BuildingWithConvertedAddresses;
          if (!building?.id) return;
          // A freshly-created AMP building-page can be centroid-less; backfill
          // the known listing centroid so focusBuilding can still centre on it.
          if (!building.centroid && coords) building.centroid = coords;
          const seed = buildPreviewSeed(buildingId);
          const map = mapHandleRef.current;
          const openCurrent = (point?: { x: number; y: number } | null) => {
            if (selectionToken !== preview.selectionRef.current) return;
            preview.open(building, point ?? undefined, seed);
          };
          if (map?.focusBuilding) {
            map.focusBuilding(building, openCurrent);
          } else {
            openCurrent();
          }
        })
        .catch(() => {});
    },
    [preview, buildPreviewSeed],
  );

  // Explore-list card click → open the same on-map preview (no click anchor, so
  // desktop docks it). Fetch the full building first, then preview its listings.
  const handleViewOnMap = useCallback(
    (result: SearchResult) => {
      openBuildingPreviewById(result.building_id, result.address?.coordinates);
    },
    [openBuildingPreviewById],
  );

  // Building view "View map" (and the mobile back arrow): return to the map with
  // this building still in context — centred and selected.
  // The camera is only worth protecting once the user has actually looked at
  // this map. A tab opened straight onto a listing or a building has kept it
  // covered since boot, so its camera is wherever the app happened to start and
  // is nobody's context to preserve (JAA-345: desktop opens listings in a new tab,
  // which made that the common case rather than a shared-link edge).
  const mapSeenRef = useRef(false);
  useEffect(() => {
    if (listingViewOpen || isBuildingViewOpen) return;
    mapSeenRef.current = true;
  }, [listingViewOpen, isBuildingViewOpen]);

  const focusBuildingOnMap = useCallback(
    (buildingId: string, coords?: LatLng) => {
      // Opened from the map: its preview card is still open behind the view and
      // the view covered the map the whole time, so the context is already
      // exactly right — re-centring would only jolt the camera.
      if (preview.building?.id === buildingId) return;
      // A deep-linked building view suspends the map, so there is nothing to
      // focus yet. Queue it for the mount that this close triggers.
      if (!mapHandleRef.current) {
        pendingMapFocusRef.current = { buildingId, coords };
        setIsMapExpanded(true);
        return;
      }
      // A map the user has never seen has no camera of theirs to keep, so give
      // it the only context this tab has: the building they were just looking at.
      if (!mapSeenRef.current) {
        openBuildingPreviewById(buildingId, coords);
        return;
      }
      // A live map the user has actually looked at means the view was opened
      // from somewhere else entirely — the feed, search, an activity row. The
      // camera behind it is wherever they parked it and is not this building's
      // context to reclaim, so closing must leave it alone.
    },
    [preview.building, openBuildingPreviewById],
  );

  useEffect(() => {
    flushPendingMapFocusRef.current = () => {
      const pending = pendingMapFocusRef.current;
      if (!pending) return;
      pendingMapFocusRef.current = null;
      openBuildingPreviewById(pending.buildingId, pending.coords);
    };
    return () => {
      flushPendingMapFocusRef.current = null;
    };
  }, [openBuildingPreviewById]);

  useEffect(() => {
    onRegisterMapFocusHandler?.(focusBuildingOnMap);
    return () => onRegisterMapFocusHandler?.(null);
  }, [onRegisterMapFocusHandler, focusBuildingOnMap]);

  // After the Add-my-place flow publishes a listing, drop the user onto the
  // expanded explore map and open the real on-map preview card for the building
  // they just added — the same behaviour as opening a listing on the map. Every
  // map-bound completion also closes a listings sheet kept open under a nav tab,
  // even when that tab (Tools) already closed itself before the flow ended (JAA-444).
  const handleAddMyPlaceComplete = useCallback(
    (summary: AddMyPlaceSummary) => {
      setIsMapExpanded(true);
      // Launched from the building view's "Sell here": that full view is still
      // on screen for the same building, so don't stack an on-map preview card
      // behind it — the view refreshes itself from the jaakko:listings-changed
      // event the publish dispatched.
      if (summary.launchedFromBuildingView) return;
      // Tools closes itself before this runs, so navViewActive cannot gate closing a sheet kept under a nav tab (JAA-444).
      if (!listingViewOpen) setShowMobileInfoSheet(false);
      // Launched from the Activity tab on mobile: leave it, or it keeps the card hidden (JAA-445).
      if (navViewActive && !listingViewOpen && !isDesktopExplorePanel) {
        onNavigateExplore();
      }
      if (summary.buildingId) {
        openBuildingPreviewById(summary.buildingId, summary.coordinates);
      }
    },
    [
      openBuildingPreviewById,
      navViewActive,
      listingViewOpen,
      isDesktopExplorePanel,
      onNavigateExplore,
    ],
  );

  useEffect(() => {
    if (userLocation) {
      setMapCenter(userLocation);
    }
  }, [userLocation]);

  useEffect(
    () => () => {
      clearGeolocateLoadingFallback();
    },
    [clearGeolocateLoadingFallback],
  );

  useEffect(
    () => () => {
      clearExploreIntroTimer();
    },
    [clearExploreIntroTimer],
  );

  // Refresh the map's listing layer when a willingness is added/removed
  // anywhere in the app. The Building modal dispatches this event after a
  // successful POST/DELETE; we re-run the same map-listings search that
  // moveend uses, so pill counts + is_new flag update immediately.
  useEffect(() => {
    const handler = () => {
      if (visibleBounds) performMapListingsSearch(visibleBounds, false);
      preview.refresh();
    };
    window.addEventListener('jaakko:listings-changed', handler);
    return () => window.removeEventListener('jaakko:listings-changed', handler);
  }, [visibleBounds, performMapListingsSearch, preview.refresh]);

  useEffect(() => {
    if (!isMapExpanded && isSearchActive) {
      setIsSearchActive(false);
    }
  }, [isMapExpanded, isSearchActive]);

  useEffect(() => {
    if (!isSearchActive) return undefined;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const insideSearch = searchSlotRefs.current.some(
        (slot) => slot && slot.contains(target),
      );
      if (insideSearch) return;
      setIsSearchActive(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isSearchActive]);

  useEffect(() => {
    if (!showMobileMenu) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (menuPanelRef.current?.contains(target)) return;
      if (target?.closest('.new-landing-page__icon-button')) return;
      setShowMobileMenu(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowMobileMenu(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMobileMenu]);

  // Surface the local full-screen overlay state (address search, Filters,
  // burger/More menu, the sellers/buyers mobile info sheet while on screen, and
  // Add-my-place flow) so the AppContent shell can hide the bottom nav on mobile
  // while any is open. A sheet covered by a nav view does not count: that view
  // needs its nav, or a chat opened from the sheet strands the user (JAA-444).
  useEffect(() => {
    onOverlayVisibilityChange?.(
      isSearchActive ||
        showFilters ||
        showMobileMenu ||
        infoSheetOnScreen ||
        addMyPlaceOpen,
    );
  }, [
    isSearchActive,
    showFilters,
    showMobileMenu,
    infoSheetOnScreen,
    addMyPlaceOpen,
    onOverlayVisibilityChange,
  ]);

  /**
   * Render the header menu through the shared modal stack (ModalOutlet), which
   * layers it above the building/profile modals (its z-index override is 2510).
   * Synced to showMobileMenu; sheetRef is forwarded so the outside-click
   * close detection above still works. Picking Send Feedback closes the menu
   * and opens the feedback modal, which lives here rather than inside the menu:
   * the menu unmounts on any outside press, and the modal is portaled to body,
   * so a modal owned by the menu vanished on the first click inside it.
   */
  const landingMenuProps = useMemo<React.ComponentProps<typeof LandingMenuModal>>(
    () => ({
      onFeedbackClick: () => {
        setShowMobileMenu(false);
        setShowFeedbackModal(true);
      },
      onClose: () => setShowMobileMenu(false),
      sheetRef: menuPanelRef,
    }),
    [],
  );

  useEffect(() => {
    if (showMobileMenu) {
      modals.open('landingMenu', landingMenuProps);
    } else {
      modals.close('landingMenu');
    }
  }, [showMobileMenu, landingMenuProps, modals]);

  // When the building view takes over: drop any open filters panel, and expand
  // the map underneath so closing the building reveals the map — not the hero
  // landing (a deep link / hero-launched open would otherwise close to the hero).
  useEffect(() => {
    if (isBuildingViewOpen) {
      setShowFilters(false);
      setIsMapExpanded(true);
    }
  }, [isBuildingViewOpen]);

  // Opening either listing-action flow must also cancel a viewport search that
  // is still inside its debounce window. Merely pausing viewport measurement
  // leaves the previous snapshot intact, so that timer would otherwise fire
  // underneath the flow and reload both the results and their counts.
  useEffect(() => {
    if (!isListingActionFlowPresent) return;
    cancelPendingMapListingsSearch(
      mapListingsFetchTimerRef,
      Boolean(mapListingsRequestRef.current),
      () => {
        setIsSearchLoading(false);
        setIsCountsLoading(false);
      },
    );
  }, [isListingActionFlowPresent]);

  useEffect(() => {
    if (
      playExploreIntro ||
      deferMapListingsSearchWhileFlowPresent() ||
      !visibleBounds
    ) {
      return undefined;
    }

    // Abort an in-flight request immediately. Pagination is invalidated only
    // when the replacement request leaves its debounce window, so opening an
    // action flow can cancel the queued search without changing the current
    // list's paging state.
    setIsSearchLoading(true);
    const activeRequest = mapListingsRequestRef.current;
    mapListingsRequestRef.current = null;
    activeRequest?.abort();

    const handle = window.setTimeout(() => {
      mapListingsFetchTimerRef.current = null;
      setPagination((prev) => ({
        ...prev,
        has_more: false,
        next_cursor: null,
      }));
      performMapListingsSearchRef.current(visibleBounds, false);
    }, MAP_LISTINGS_FETCH_DEBOUNCE_MS);
    mapListingsFetchTimerRef.current = handle;

    return () => {
      window.clearTimeout(handle);
      if (mapListingsFetchTimerRef.current === handle) {
        mapListingsFetchTimerRef.current = null;
      }
    };
    // Deliberately NOT depending on performMapListingsSearch: picking a search
    // result (or "Use current location") sets the search preference, which rebuilt
    // that callback and refetched the OLD viewport a second before the map
    // finished flying — the panel reloaded twice for one action. The camera and
    // filters and measured overlay geometry change which listings belong here;
    // the preference rides along in the ref and applies to the fetch the new
    // viewport triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listingViewport,
    searchFilters,
    playExploreIntro,
    deferredMapListingsSearchRevision,
    deferMapListingsSearchWhileFlowPresent,
  ]);

  // Reverse-geocode the visible map center on every (debounced) moveend so the
  // info-panel header reflects whichever area the user is currently looking at.
  // Search-derived labels still win in the moment because `setSearchAddress
  // Components` updates `areaLabel` synchronously; this effect catches up after
  // the post-flyTo moveend with the same or a slightly more specific area.
  //
  // Finland-only fallback: when Google's reverse-geocode has no neighborhood-
  // level result, but does carry a postal_code, look up the postal in our
  // backend (postal_codes.json) for candidate neighborhoods, then forward-
  // geocode each candidate via Google to get a centroid and pick the closest
  // to the map center. Resolves Käpylä, Toukola, Vallila, Sörnäinen, etc.
  useEffect(() => {
    if (!isGoogleMapsLoaded || !lastMapBoundaryBoxCenter) return undefined;
    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
    const geocoder = geocoderRef.current;
    const center = lastMapBoundaryBoxCenter;

    const fetchNeighborhoodsForPostal = async (
      postalCode: string,
    ): Promise<string[]> => {
      const cached = postalNeighborhoodsCacheRef.current.get(postalCode);
      if (cached) return cached;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/postal-codes/${encodeURIComponent(postalCode)}`,
        );
        if (!res.ok) {
          postalNeighborhoodsCacheRef.current.set(postalCode, []);
          return [];
        }
        const data: { neighbourhoods?: string[] } = await res.json();
        const list = data.neighbourhoods ?? [];
        postalNeighborhoodsCacheRef.current.set(postalCode, list);
        return list;
      } catch {
        return [];
      }
    };

    const resolveCentroid = (
      postalCode: string,
      name: string,
      locality: string,
    ): Promise<LatLng | null> => {
      const cacheKey = `${postalCode}|${name}`;
      const cached = neighborhoodCentroidCacheRef.current.get(cacheKey);
      if (cached) return Promise.resolve(cached);
      return new Promise((resolve) => {
        geocoder.geocode(
          {
            address: `${name}, ${locality}, Finland`,
            componentRestrictions: { country: 'FI' },
          },
          (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || !results?.length) {
              resolve(null);
              return;
            }
            const loc = results[0].geometry.location;
            const centroid = { lat: loc.lat(), lng: loc.lng() };
            neighborhoodCentroidCacheRef.current.set(cacheKey, centroid);
            resolve(centroid);
          },
        );
      });
    };

    const handle = window.setTimeout(() => {
      geocoder.geocode(
        { location: { lat: center.lat, lng: center.lng } },
        (geocodeResults, status) => {
          if (
            status !== google.maps.GeocoderStatus.OK ||
            !geocodeResults ||
            geocodeResults.length === 0
          ) {
            return;
          }
          // Primary path: Google's response itself contains a neighborhood-
          // level result (Kallio, Kamppi, Punavuori, Töölö, ...).
          const neighborhoodResult = geocodeResults.find((r) => {
            const types = r.types || [];
            return (
              types.includes('neighborhood') ||
              types.includes('colloquial_area') ||
              types.some((t) => t.startsWith('sublocality'))
            );
          });
          if (neighborhoodResult) {
            setAreaLabel(
              extractAreaLabel(neighborhoodResult.address_components),
            );
            console.log('[NewLandingPage reverse-geocode]', {
              center,
              path: 'reverse-neighborhood',
              chosenIndex: geocodeResults.indexOf(neighborhoodResult),
              chosenTypes: neighborhoodResult.types,
              resultsCount: geocodeResults.length,
            });
            return;
          }

          // Build the legacy fallback (results[0]) up-front so every
          // non-Finland or no-postal branch can short-circuit to it.
          const fallbackLabel = extractAreaLabel(
            geocodeResults[0].address_components,
          );
          const postalResult = geocodeResults.find((r) =>
            (r.types || []).includes('postal_code'),
          );
          const postalCode =
            postalResult?.address_components.find((c) =>
              c.types.includes('postal_code'),
            )?.long_name ?? null;
          const flat = geocodeResults.flatMap((r) => r.address_components);
          const locality =
            flat.find((c) => c.types.includes('locality'))?.long_name ??
            'Helsinki';
          const region =
            flat.find((c) => c.types.includes('administrative_area_level_1'))
              ?.long_name ?? null;
          const country =
            flat.find((c) => c.types.includes('country'))?.long_name ?? null;

          // No postal code anywhere in the response — nothing better than the
          // legacy locality-level fallback. Same for Finland and non-Finland.
          if (!postalCode) {
            setAreaLabel(fallbackLabel);
            console.log('[NewLandingPage reverse-geocode]', {
              center,
              path: 'fallback-no-postal',
              countryOfFeedContent,
              resultsCount: geocodeResults.length,
            });
            return;
          }

          // Non-Finland: surface the postal code on the primary line and let
          // the existing postalText filter strip the duplicate, leaving just
          // the city on the secondary line. The locality default of
          // 'Helsinki' above is inappropriate here, so re-derive without it.
          if (countryOfFeedContent !== 'Finland') {
            const nonFinlandCity =
              flat.find((c) => c.types.includes('locality'))?.long_name ?? null;
            // Combine postal + city onto the primary line so the header reads
            // e.g. "UB3 1NB, Hayes" instead of postal on top and city on a
            // separate secondary line. `city` stays populated so the zoom-out
            // collapse can fall through to just the city at low zoom.
            const combinedArea = nonFinlandCity
              ? `${postalCode}, ${nonFinlandCity}`
              : postalCode;
            setAreaLabel({
              area: combinedArea,
              postal: null,
              city: nonFinlandCity,
              region,
              country,
            });
            console.log('[NewLandingPage reverse-geocode]', {
              center,
              path: 'fallback-non-finland-postal',
              countryOfFeedContent,
              postalCode,
              nonFinlandCity,
              combinedArea,
            });
            return;
          }

          const myAttemptId = ++reverseGeocodeAttemptIdRef.current;
          (async () => {
            const neighborhoods = await fetchNeighborhoodsForPostal(postalCode);
            if (myAttemptId !== reverseGeocodeAttemptIdRef.current) return;
            if (neighborhoods.length === 0) {
              setAreaLabel(fallbackLabel);
              console.log('[NewLandingPage reverse-geocode]', {
                center,
                path: 'fallback-postal-no-data',
                postalCode,
              });
              return;
            }
            if (neighborhoods.length === 1) {
              setAreaLabel({
                area: neighborhoods[0],
                postal: postalCode,
                city: locality,
                region,
                country,
              });
              console.log('[NewLandingPage reverse-geocode]', {
                center,
                path: 'fallback-postal-resolved',
                postalCode,
                neighborhoods,
                chosen: neighborhoods[0],
              });
              return;
            }
            const centroids = await Promise.all(
              neighborhoods.map((name) =>
                resolveCentroid(postalCode, name, locality),
              ),
            );
            if (myAttemptId !== reverseGeocodeAttemptIdRef.current) return;
            let bestName: string | null = null;
            let bestDistSq = Infinity;
            centroids.forEach((c, i) => {
              if (!c) return;
              const dLat = c.lat - center.lat;
              const dLng = c.lng - center.lng;
              const distSq = dLat * dLat + dLng * dLng;
              if (distSq < bestDistSq) {
                bestDistSq = distSq;
                bestName = neighborhoods[i];
              }
            });
            if (myAttemptId !== reverseGeocodeAttemptIdRef.current) return;
            if (bestName) {
              setAreaLabel({
                area: bestName,
                postal: postalCode,
                city: locality,
                region,
                country,
              });
              console.log('[NewLandingPage reverse-geocode]', {
                center,
                path: 'fallback-postal-resolved',
                postalCode,
                neighborhoods,
                chosen: bestName,
              });
            } else {
              setAreaLabel(fallbackLabel);
              console.log('[NewLandingPage reverse-geocode]', {
                center,
                path: 'fallback-postal-no-centroid',
                postalCode,
                neighborhoods,
              });
            }
          })();
        },
      );
    }, REVERSE_GEOCODE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [isGoogleMapsLoaded, lastMapBoundaryBoxCenter, countryOfFeedContent]);

  useLayoutEffect(() => {
    const node = heroContentRef.current;
    if (!node) return undefined;

    const update = () => {
      setHeroContentHeight(node.getBoundingClientRect().height);
    };

    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const areaTier = resolveAreaTier(lastZoomLevel);
  const areaFallback = t('newLandingPage.explore.areaFallback');
  const worldLabel = t('newLandingPage.explore.continents.world');
  // Continent lookup uses countryOfFeedContent (canonical English), not the
  // possibly-localized areaLabel.country, so it resolves in any UI language.
  const continentKey = COUNTRY_TO_CONTINENT[countryOfFeedContent];
  const continentLabel = continentKey
    ? t(`newLandingPage.explore.continents.${continentKey}`)
    : null;

  let areaText: string;
  switch (areaTier) {
    case 'city':
      areaText = areaLabel.city || areaLabel.area || areaFallback;
      break;
    case 'region':
      areaText =
        areaLabel.region || areaLabel.city || areaLabel.area || areaFallback;
      break;
    case 'country':
      areaText = areaLabel.country || areaLabel.region || areaFallback;
      break;
    case 'continent':
      // Unknown continent (unmapped country) falls through to country, then World.
      areaText = continentLabel || areaLabel.country || worldLabel;
      break;
    case 'world':
      areaText = worldLabel;
      break;
    case 'neighborhood':
    default:
      areaText = areaLabel.area || areaFallback;
      break;
  }

  // Secondary postal line stays only at the most specific tier, matching the
  // old showCityOnly === false behavior.
  const postalText =
    areaTier !== 'neighborhood'
      ? null
      : (() => {
          const parts = [areaLabel.postal, areaLabel.city]
            .filter((part): part is string => Boolean(part))
            // Generalised dedupe: skip any part already contained in the primary
            // line, so e.g. "Hayes" doesn't repeat below "UB3 1NB, Hayes".
            .filter(
              (part) => !areaLabel.area || !areaLabel.area.includes(part),
            );
          return parts.length > 0 ? parts.join(' ') : null;
        })();

  // District labels around a point for the explore search's suggestion rows,
  // read from the map's loaded tiles (no request); [] before the map mounts.
  const handleNearbyPlaces = useCallback(
    (center: LatLng) =>
      mapHandleRef.current?.nearbyPlaceLabels(center, NEARBY_PLACES_RADIUS_M) ?? Promise.resolve([]),
    [],
  );

  const handleSearchAddressComponents = useCallback(
    (components: Array<{ long_name: string; types: string[] }>) => {
      const nextAreaLabel = extractAreaLabel(components);

      console.log('[NewLandingPage] address search components received', {
        addressComponents: components,
        areaLabel: nextAreaLabel,
      });

      setAreaLabel(nextAreaLabel);
    },
    [],
  );

  // Hovering a demand card highlights its area shape — the 500 m circle, or
  // the district polygon (from the already-loaded demand neighbourhoods). The
  // anchor building (green outline) lights up only for a circle (building +
  // radius) interest: a district-only interest still stores the anchor id the
  // flow started from, but the building has no relation to what the buyer
  // wants, so its highlight would mislead. Both clear on mouse-leave.
  const handleHoverDemandCard = useCallback((card: AreaDemandCard) => {
    const circle = card.display_areas.find((a) => a.kind === 'circle');
    if (circle && card.anchor_building_id) {
      mapHandleRef.current?.highlightBuilding(card.anchor_building_id);
    }
    const map = demandMapRef.current;
    if (!map) return;
    // The circle and every picked area light up together: one interest can hold both,
    // and Kallio + Sornainen once outlined Kallio alone.
    const shapes = card.display_areas
      .filter((a) => a.kind === 'area')
      .map((a) => {
        const area = a as { ext_id?: string; name?: string };
        return neighbourhoodGeometry(area.ext_id ? areaIdKey(area.ext_id) : '', area.name ?? '');
      })
      .filter((g): g is GeoJSON.Geometry => g !== null);
    if (circle && card.anchor_lng != null && card.anchor_lat != null) {
      const radius = (circle as { radius_m?: number }).radius_m ?? 500;
      shapes.push(circlePolygon(card.anchor_lng, card.anchor_lat, radius));
    }
    setAreaHighlight(map, mergePolygons(shapes));
  }, []);

  const handleHoverDemandEnd = useCallback(() => {
    mapHandleRef.current?.highlightBuilding(preview.building?.id ?? null);
    if (demandMapRef.current) setAreaHighlight(demandMapRef.current, null);
  }, [preview.building]);

  /**
   * The explore panel body: skeleton cards while a search loads, else the
   * listing grid. Shared by the mobile info sheet and the desktop info panel;
   * `variant` only turns on hover-to-highlight for desktop.
   */
  const renderExploreContent = (variant: 'mobile' | 'desktop') => {
    if (isSearchLoading) {
      return (
        <div className="new-landing-page__explore-loading-skeletons">
          <ListingCardSkeleton count={3} />
        </div>
      );
    }
    return (
      <SearchResults
        results={confirmedResults}
        secondaryResults={unconfirmedResults}
        secondaryTotalResults={matchCounts.unconfirmed}
        hasDetailFilters={activeDetailFields(searchFilters).length > 0}
        resetKey={listResetKey}
        totalResults={pagination.total}
        isLoadingMore={isLoadingMore}
        hasMore={pagination.has_more}
        noFiltersSet={noFiltersSet}
        onLoadMore={handleLoadMore}
        onViewOnMap={handleViewOnMap}
        onSelectBuilding={handleExploreSelect}
        onOpenListing={openListing}
        onCouldBuy={handleCouldBuyFromCard}
        onHoverListing={variant === 'desktop' ? handleHoverListing : undefined}
        onRemoveResult={handleRemoveResult}
        performSearch={performSearch}
        showRemoveListingDialog={showRemoveListingDialog}
        setShowRemoveListingDialog={setShowRemoveListingDialog}
        isLoggedIn={isLoggedIn}
        setLoginModalVisible={setLoginModalVisible}
        isAdmin={isAdmin}
        countryOfFeedContent={countryOfFeedContent}
        renderExtraCard={(result) => (
          <DemandBuyerCard
            card={result as unknown as AreaDemandCard}
            isLoggedIn={isLoggedIn}
            setLoginModalVisible={setLoginModalVisible}
            onOpen={openListing}
            onHover={variant === 'desktop' ? handleHoverDemandCard : undefined}
            onHoverEnd={
              variant === 'desktop' ? handleHoverDemandEnd : undefined
            }
          />
        )}
      />
    );
  };

  // Picker fallback for a seedless could-buy (Activity FAB, JAA-341): the live
  // main-map centre when it is well-formed, else the IP-located start centre —
  // the same chain the AddMyPlaceFlow uses above. Editing an existing area
  // interest or buy listing keeps the legacy default instead; the map's
  // position is unrelated to what is being edited.
  const liveMapCentre =
    lastMapBoundaryBoxCenter &&
    Number.isFinite(lastMapBoundaryBoxCenter.lat) &&
    Number.isFinite(lastMapBoundaryBoxCenter.lng)
      ? lastMapBoundaryBoxCenter
      : null;
  const couldBuyFallbackCenter =
    couldBuyInterestId || couldBuyWillingnessId ? null : liveMapCentre ?? mapCenter;
  const initialLandingMapZoom =
    countryOfFeedContent === 'Finland' ? FINLAND_LANDING_MAP_ZOOM : 15;
  // Only the active breakpoint mounts this card, so its hint ID stays unique.
  const sellingCta = (
    <div data-map-occluder="cta" className="new-landing-page__explore-hint-card">
      <div className="new-landing-page__explore-hint-row">
        <img
          alt=""
          aria-hidden="true"
          className="new-landing-page__explore-hint-avatar"
          src={`${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`}
        />
        <span id="explore-selling-hint" className="new-landing-page__explore-hint-text">
          {t('newLandingPage.explore.actions.sellAdvice')}
        </span>
      </div>
      <button
        className="new-landing-page__explore-test-value"
        aria-describedby="explore-selling-hint"
        onClick={() => openAddMyPlaceWithSeed(null)}
        type="button"
      >
        {t('newLandingPage.addMyPlace.title')}
      </button>
    </div>
  );

  // Rendered in both bottom stacks; CSS shows only the one for the current breakpoint.
  const zoomInHint = mapFeaturesTruncated ? (
    <p className="new-landing-page__zoom-in-hint" role="status">
      {t('newLandingPage.explore.zoomInToSeeAll')}
    </p>
  ) : null;

  return (
    <>
    <MapOnboardingHint
      map={onboardingMap}
      isLoggedIn={isLoggedIn}
      hasCheckedLogin={hasCheckedLogin}
      enabled={hasStartedBrowsing && isMapExpanded && !playExploreIntro}
      settleDelayMs={isDesktopExplorePanel ? 0 : 1500}
      locationRevision={mapHintLocationRevision}
      blocked={
        isSearchActive || showFilters || showMobileMenu || showMobileInfoSheet ||
        showFeedbackModal || isListingActionFlowPresent || Boolean(preview.building) ||
        navViewActive || isBuildingViewOpen || listingViewOpen ||
        isGeolocating || !!showRemoveListingDialog ||
        modalStack.length > 0
      }
    />
    <OffMarketToast message={toastMessage} onDismiss={() => setToastMessage(null)} />
    <FeedbackModal
      isOpen={showFeedbackModal}
      onClose={() => setShowFeedbackModal(false)}
      isLoggedIn={isLoggedIn}
      userEmail={userEmail || undefined}
    />
    <div
      className="new-landing-page"
      data-map-expanded={isMapExpanded ? 'true' : 'false'}
      data-explore-intro={playExploreIntro ? 'true' : 'false'}
      data-explore-controls-hidden={isBuildingViewOpen ? 'true' : 'false'}
      data-info-sheet-open={infoSheetOnScreen ? 'true' : 'false'}
      data-search-active={isSearchActive ? 'true' : 'false'}
      data-header-map-controls-hidden={hideHeaderMapControls ? 'true' : 'false'}
      style={
        heroContentHeight !== null
          ? ({
              '--new-landing-hero-content-height': `${heroContentHeight}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <header data-map-occluder="header" className="new-landing-page__header">
        <button
          className="new-landing-page__logo"
          onClick={handleLogoClick}
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="84"
            height="18"
            viewBox="0 0 84 18"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M76.8565 3.73145C81.3397 3.75951 84.029 6.778 83.999 10.8467L84.001 10.8496H84C83.9921 15.1707 81.127 17.8993 76.667 17.9395C73.0033 17.9728 69.5086 15.2988 69.6201 10.6553C69.7139 6.6957 72.4354 3.70354 76.8565 3.73145ZM11.583 6.25977C13.1677 3.76935 17.5363 2.67381 20.4531 5.50586V5.50293C20.481 4.97286 20.5021 4.57533 20.5244 4.12891H24.375V17.4961H20.5322C20.5077 17.0909 20.4836 16.6941 20.459 16.2979C20.3931 16.2867 20.3022 16.2471 20.2832 16.2705C17.7258 19.1512 12.0465 18.1958 10.6572 13.501C9.90346 10.9513 10.1313 8.54151 11.583 6.25977ZM28.249 5.43262C30.4121 3.30407 33.7689 3.24211 36.0547 5.25586C36.1172 5.31046 36.1977 5.34362 36.3125 5.41602V5.41504C36.3505 4.97193 36.382 4.57984 36.4199 4.13672H40.2471V17.4766H36.418C36.3856 17.1095 36.3493 16.7246 36.3047 16.2158C35.0172 17.4905 33.5175 18.0484 31.8291 17.833C28.5203 17.4088 26.3796 14.9874 26.1875 11.5234C26.0591 9.2284 26.5439 7.10926 28.249 5.43262ZM8.18556 0.550781C8.23788 0.635291 8.29756 0.686254 8.29786 0.736328C8.29786 4.94807 8.33654 9.16242 8.26954 13.373C8.23381 15.5621 6.4685 17.355 4.06544 17.6855C2.93422 17.8418 1.76253 17.7481 0.612316 17.7012C0.403663 17.6933 0.0437386 17.377 0.0341914 17.1895C-0.0205234 16.0766 0.0068437 14.961 0.00684769 13.8838C0.823147 13.9296 1.53366 13.9957 2.24611 13.999C3.62955 14.0068 4.1719 13.4818 4.17872 12.0801C4.19547 8.61615 4.18654 5.15045 4.18654 1.68652V0.551758H4.18458V0.550781H8.18556ZM46.6279 9.65039L51.1279 4.08789C51.1485 4.08783 54.2665 4.0791 55.8125 4.0791C53.8472 6.43775 52.0516 8.59436 50.208 10.8057C52.1175 12.9934 56.0878 17.5461 56.0928 17.5518H51.1973L46.6875 11.9521V17.4912H42.7793V0H46.6279V9.65039ZM60.794 9.65039L65.294 4.08789C65.3144 4.08783 68.4325 4.0791 69.9785 4.0791C68.0132 6.43775 66.2176 8.59436 64.374 10.8057C66.2837 12.9936 70.2545 17.5468 70.2588 17.5518H65.3633L60.8535 11.9521V17.4912H56.9453V0H60.794V9.65039ZM76.7813 7.34082C74.8908 7.35981 73.5637 8.7985 73.5625 10.8301C73.5625 12.8962 74.9519 14.3936 76.8467 14.3682C78.7105 14.3414 80.0596 12.8761 80.0752 10.8623C80.0896 8.81628 78.6918 7.32408 76.7813 7.34082ZM33.3233 7.34082C31.4491 7.32745 30.1114 8.78415 30.1113 10.8379H30.1123C30.1123 12.8685 31.5078 14.3788 33.3447 14.3408C35.1626 14.3027 36.5103 12.8306 36.5215 10.8682C36.5325 8.82768 35.197 7.35422 33.3233 7.34082ZM17.4004 7.33789C15.559 7.3111 14.1346 8.82658 14.1279 10.8203C14.1224 12.7906 15.5613 14.3499 17.376 14.3398C19.2039 14.3308 20.5392 12.8874 20.5693 10.8926H20.5684C20.5979 8.84936 19.2738 7.36471 17.4004 7.33789Z"
              fill="black"
            />
          </svg>
        </button>

        {isMapExpanded && (
          <div className="new-landing-page__header-search-filter">
            <div
              data-map-occluder="search"
              className="new-landing-page__header-search-slot"
              ref={(node) => registerSearchSlot(node, 0)}
            >
              <SearchInput
                setMapCenter={handleSearchMapCenter}
                setPreferredLocation={setPreferredLocation}
                setTriggeredFromSearch={setTriggeredFromSearch}
                setSearchResultBounds={setSearchResultBounds}
                setSearchAddressComponents={handleSearchAddressComponents}
                setCountryOfFeedContent={setCountryOfFeedContent}
                countryOfFeedContent={countryOfFeedContent}
                placeholder={t('newLandingPage.explore.search')}
                isActive={isSearchActive}
                onActivate={handleSearchActivate}
                onDeactivate={handleSearchDeactivate}
                debugLabel="NewLandingPage header SearchInput"
                viewportCenter={lastMapBoundaryBoxCenter ?? mapCenter}
                viewportZoom={lastZoomLevel}
                getNearbyPlaces={handleNearbyPlaces}
                areaCity={areaLabel.city}
              />
            </div>
            <button
              aria-label={t('newLandingPage.explore.filterAria')}
              data-map-occluder="filter"
              className="new-landing-page__header-search-filter-pill new-landing-page__header-search-filter-pill--filter"
              onClick={() => setShowFilters(true)}
              type="button"
            >
              <FilterIcon />
              <span>{t('newLandingPage.explore.filter')}</span>
              {appliedFiltersCount > 0 && (
                <span
                  className="new-landing-page__filters-badge"
                  aria-label={`${appliedFiltersCount} filters applied`}
                >
                  {appliedFiltersCount}
                </span>
              )}
            </button>
          </div>
        )}

        <div className="new-landing-page__header-actions new-landing-page__header-actions--desktop">
          <button
            className="new-landing-page__language-button"
            onClick={handleToggleLanguage}
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 23C14.1756 23 16.3023 22.3549 18.1113 21.1462C19.9202 19.9375 21.3301 18.2195 22.1627 16.2095C22.9952 14.1995 23.2131 11.9878 22.7886 9.85401C22.3642 7.72022 21.3165 5.76021 19.7782 4.22183C18.2398 2.68345 16.2798 1.6358 14.146 1.21137C12.0122 0.786929 9.80047 1.00477 7.79048 1.83733C5.78049 2.66989 4.06253 4.07979 2.85383 5.88873C1.64514 7.69767 1 9.82441 1 12C1 14.9174 2.15892 17.7153 4.22182 19.7782C6.28472 21.8411 9.08262 23 12 23ZM3.26967 13.1H7.25167C7.314 14.9663 7.57433 17.8667 8.49466 20.0667C7.09547 19.4567 5.87848 18.4941 4.96265 17.273C4.04682 16.0519 3.46349 14.614 3.26967 13.1ZM12 3.2C13.309 3.2 14.387 6.55867 14.5447 10.9H9.45533C9.613 6.55867 10.691 3.2 12 3.2ZM14.5447 13.1C14.387 17.4413 13.309 20.8 12 20.8C10.691 20.8 9.613 17.4413 9.45533 13.1H14.5447ZM15.5053 20.0667C16.4257 17.8667 16.6823 14.9663 16.7483 13.1H20.7303C20.5365 14.614 19.9532 16.0519 19.0373 17.273C18.1215 18.4941 16.9045 19.4567 15.5053 20.0667ZM20.7303 10.9H16.7483C16.686 9.03367 16.4257 6.13334 15.5053 3.93334C16.9045 4.54333 18.1215 5.50593 19.0373 6.72704C19.9532 7.94814 20.5365 9.38598 20.7303 10.9ZM8.49466 3.93334C7.57433 6.13334 7.31767 9.03367 7.25167 10.9H3.26967C3.46349 9.38598 4.04682 7.94814 4.96265 6.72704C5.87848 5.50593 7.09547 4.54333 8.49466 3.93334Z"
                fill="#333333"
              />
            </svg>
            <span>
              {languageIsFinnish
                ? t('newLandingPage.header.languageEnglish')
                : t('newLandingPage.header.languageFinnish')}
            </span>
          </button>

          {isLoggedIn ? (
            <button
              aria-label={t('newLandingPage.header.openProfileAria')}
              className="new-landing-page__me-button"
              onClick={handleOpenProfile}
              type="button"
            >
              <MeIcon />
            </button>
          ) : (
            <button
              className="new-landing-page__sign-in-button"
              onClick={() => setLoginModalVisible(true)}
              type="button"
            >
              {t('newLandingPage.header.signIn')}
            </button>
          )}

          <button
            aria-label={t('newLandingPage.header.openMenuAria')}
            className="new-landing-page__icon-button"
            onClick={() => setShowMobileMenu((prev) => !prev)}
            type="button"
          >
            <MoreIcon />
          </button>
        </div>

        <div className="new-landing-page__header-actions new-landing-page__header-actions--mobile">
          <button
            className="new-landing-page__language-button new-landing-page__language-button--mobile"
            aria-label={
              languageIsFinnish
                ? t('newLandingPage.header.switchLanguageToEnglish')
                : t('newLandingPage.header.switchLanguageToFinnish')
            }
            onClick={handleToggleLanguage}
            type="button"
          >
            <span>{languageIsFinnish ? 'EN' : 'FI'}</span>
          </button>

          {isLoggedIn ? (
            <button
              aria-label={t('newLandingPage.header.openProfileAria')}
              className="new-landing-page__me-button new-landing-page__me-button--mobile"
              onClick={handleOpenProfile}
              type="button"
            >
              <MeIcon />
            </button>
          ) : (
            <button
              className="new-landing-page__sign-in-button new-landing-page__sign-in-button--mobile"
              onClick={() => setLoginModalVisible(true)}
              type="button"
            >
              {t('newLandingPage.header.signIn')}
            </button>
          )}

          <button
            aria-label={t('newLandingPage.header.openMenuAria')}
            className="new-landing-page__icon-button"
            onClick={() => setShowMobileMenu((prev) => !prev)}
            type="button"
          >
            <MoreIcon />
          </button>
        </div>
      </header>

      <FilterPanel
        filters={searchFilters}
        onFiltersChange={handleFiltersChange}
        showFilters={showFilters}
        onClose={() => setShowFilters(false)}
      />
      {navViewActive && (
        <div className="new-landing-page__nav-backdrop" aria-hidden="true" />
      )}
      {isDesktopLandingMap ? (
        <div className="new-landing-page__desktop-map">
          {!shouldSuspendLandingMap && (
            <BuildingMap
              ref={mapHandleRef}
              hideGeolocateControl
              mapCenter={mapCenter}
              selectedBuilding={selectedBuilding}
              triggeredFromSearch={triggeredFromSearch}
              searchResultBounds={searchResultBounds}
              feedIsOpen={false}
              mobileFeedIsOpen={false}
              countryOfFeedContent={countryOfFeedContent}
              lastMapBoundaryBoxCenter={lastMapBoundaryBoxCenter}
              lastZoomLevel={lastZoomLevel}
              initialMapZoom={initialLandingMapZoom}
              isLandingPage
              declutterSellMarkers
              offMarketFeatureCollection={filteredFeatureCollection}
              onGeolocateEnd={handleGeolocateEnd}
              onMapReady={handleMapReady}
              onGeolocateStart={handleGeolocateStart}
              onMapMoveStart={handleMapListingsMoveStart}
              onMapMoveDiscarded={handleMapListingsMoveDiscarded}
              onUserMapInteraction={handleMapUserInteraction}
              onVisibleBoundsChange={handleVisibleBoundsChange}
              setShowFeed={() => {}}
              setTriggeredFromSearch={setTriggeredFromSearch}
              selectBuilding={handleBuildingSelect}
              openPreviewOnClick
              selectionRef={preview.selectionRef}
              setCountryOfFeedContent={setCountryOfFeedContent}
              setLastMapBoundaryBoxCenter={setLastMapBoundaryBoxCenter}
              setLastZoomLevel={setLastZoomLevel}
            />
          )}
          <div className="new-landing-page__desktop-gradient new-landing-page__desktop-gradient--radial" />
          <div className="new-landing-page__desktop-gradient new-landing-page__desktop-gradient--sand" />
          <div className="new-landing-page__desktop-gradient new-landing-page__desktop-gradient--white" />
          {!isMapExpanded && (
            <button
              aria-label={t('newLandingPage.hero.startExploring')}
              className="new-landing-page__map-expand-trigger"
              onClick={handleExpandMap}
              onTouchCancel={handleMapTouchCancel}
              onTouchEnd={handleMapTouchEnd}
              onTouchMove={handleMapTouchMove}
              onTouchStart={handleMapTouchStart}
              onWheel={handleExpandMap}
              type="button"
            />
          )}
        </div>
      ) : (
        <div className="new-landing-page__mobile-map-card">
          <div className="new-landing-page__mobile-map-frame">
            <div className="new-landing-page__mobile-map-top-glow" />
            {!shouldSuspendLandingMap && (
              <BuildingMap
                ref={mapHandleRef}
                hideGeolocateControl
                mapCenter={mapCenter}
                selectedBuilding={selectedBuilding}
                triggeredFromSearch={triggeredFromSearch}
                searchResultBounds={searchResultBounds}
                feedIsOpen={false}
                mobileFeedIsOpen={false}
                countryOfFeedContent={countryOfFeedContent}
                lastMapBoundaryBoxCenter={lastMapBoundaryBoxCenter}
                lastZoomLevel={lastZoomLevel}
                initialMapZoom={initialLandingMapZoom}
                isLandingPage
                isSmallMap
                declutterSellMarkers
                offMarketFeatureCollection={filteredFeatureCollection}
                onGeolocateEnd={handleGeolocateEnd}
                onMapReady={handleMapReady}
                onGeolocateStart={handleGeolocateStart}
                onMapMoveStart={handleMapListingsMoveStart}
              onMapMoveDiscarded={handleMapListingsMoveDiscarded}
                onUserMapInteraction={handleMapUserInteraction}
                onVisibleBoundsChange={handleVisibleBoundsChange}
                setShowFeed={() => {}}
                setTriggeredFromSearch={setTriggeredFromSearch}
                selectBuilding={handleBuildingSelect}
                openPreviewOnClick
                selectionRef={preview.selectionRef}
                setCountryOfFeedContent={setCountryOfFeedContent}
                setLastMapBoundaryBoxCenter={setLastMapBoundaryBoxCenter}
                setLastZoomLevel={setLastZoomLevel}
              />
            )}
            <div className="new-landing-page__mobile-map-expanded-gradient new-landing-page__mobile-map-expanded-gradient--top" />
            {isLoggedIn && (
              <div className="new-landing-page__mobile-map-expanded-gradient new-landing-page__mobile-map-expanded-gradient--bottom" />
            )}
            <div className="new-landing-page__desktop-gradient new-landing-page__desktop-gradient--radial" />
            <div className="new-landing-page__desktop-gradient new-landing-page__desktop-gradient--sand" />
            <div className="new-landing-page__desktop-gradient new-landing-page__desktop-gradient--white" />
            {!isMapExpanded && (
              <button
                aria-label={t('newLandingPage.hero.startExploring')}
                className="new-landing-page__map-expand-trigger"
                onClick={handleExpandMap}
                onTouchCancel={handleMapTouchCancel}
                onTouchEnd={handleMapTouchEnd}
                onTouchMove={handleMapTouchMove}
                onTouchStart={handleMapTouchStart}
                onWheel={handleExpandMap}
                type="button"
              />
            )}
          </div>
        </div>
      )}

      {isMapExpanded && (
        <>
          <div className="new-landing-page__explore-controls">
            <div
              data-map-occluder="search"
              className="new-landing-page__explore-search-slot"
              ref={(node) => registerSearchSlot(node, 1)}
            >
              <SearchInput
                setMapCenter={handleSearchMapCenter}
                setPreferredLocation={setPreferredLocation}
                setTriggeredFromSearch={setTriggeredFromSearch}
                setSearchResultBounds={setSearchResultBounds}
                setSearchAddressComponents={handleSearchAddressComponents}
                setCountryOfFeedContent={setCountryOfFeedContent}
                countryOfFeedContent={countryOfFeedContent}
                placeholder={t('newLandingPage.explore.search')}
                isActive={isSearchActive}
                onActivate={handleSearchActivate}
                onDeactivate={handleSearchDeactivate}
                debugLabel="NewLandingPage explore SearchInput"
                viewportCenter={lastMapBoundaryBoxCenter ?? mapCenter}
                viewportZoom={lastZoomLevel}
                getNearbyPlaces={handleNearbyPlaces}
                areaCity={areaLabel.city}
              />
            </div>
            <button
              aria-label={t('newLandingPage.explore.filterAria')}
              data-map-occluder="filter"
              className={`new-landing-page__explore-pill new-landing-page__explore-pill--filter${appliedFiltersCount > 0 ? ' new-landing-page__explore-pill--has-applied-filters' : ''}`}
              onClick={() => setShowFilters(true)}
              type="button"
            >
              <FilterIcon />
              <span>{t('newLandingPage.explore.filter')}</span>
              {appliedFiltersCount > 0 && (
                <span
                  className="new-landing-page__filters-badge"
                  aria-label={`${appliedFiltersCount} filters applied`}
                >
                  {appliedFiltersCount}
                </span>
              )}
            </button>
            <div className="new-landing-page__mobile-control-stack">
              <button
                aria-busy={!showMobileInfoSheet && isGeolocating}
                aria-label={
                  showMobileInfoSheet
                    ? t('newLandingPage.explore.closeListAria')
                    : t('newLandingPage.explore.geolocateAria')
                }
                data-map-occluder="gps"
                className={`new-landing-page__explore-geolocate${showMobileInfoSheet ? ' new-landing-page__explore-geolocate--close' : ''}${!showMobileInfoSheet && isGeolocating ? ' new-landing-page__explore-geolocate--loading' : ''}`}
                disabled={!showMobileInfoSheet && isGeolocating}
                onClick={
                  showMobileInfoSheet
                    ? handleCloseMobileInfoSheet
                    : handleGeolocate
                }
                type="button"
              >
                <span
                  className="new-landing-page__explore-geolocate-icon"
                  data-active={!showMobileInfoSheet ? 'true' : 'false'}
                  aria-hidden={showMobileInfoSheet}
                >
                  <GeolocateArrowIcon />
                </span>
                <span
                  className="new-landing-page__explore-geolocate-icon new-landing-page__explore-geolocate-icon--close"
                  data-active={showMobileInfoSheet ? 'true' : 'false'}
                  aria-hidden={!showMobileInfoSheet}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.1689 0.335607C13.6164 -0.111869 14.3417 -0.111869 14.7892 0.335607C15.2366 0.78309 15.2367 1.50844 14.7892 1.95589L9.18269 7.56241L14.7892 13.1689C15.2366 13.6164 15.2367 14.3418 14.7892 14.7892C14.3418 15.2367 13.6164 15.2366 13.1689 14.7892L7.56241 9.18269L1.95589 14.7892C1.50844 15.2367 0.78309 15.2366 0.335607 14.7892C-0.111868 14.3417 -0.111869 13.6164 0.335607 13.1689L5.94213 7.56241L0.335607 1.95589C-0.111869 1.50841 -0.111869 0.783083 0.335607 0.335607C0.783083 -0.111869 1.50841 -0.111869 1.95589 0.335607L7.56241 5.94213L13.1689 0.335607Z"
                      fill="#333333"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>

          {!isBuildingViewOpen && (
            <div className="new-landing-page__explore-bottom">
              {zoomInHint}
              <button
                aria-busy={isCountsLoading}
                aria-label={t('newLandingPage.explore.sellersBuyersAria')}
                className="new-landing-page__sellers-buyers-pill"
                onClick={handleOpenMobileInfoSheet}
                type="button"
              >
                <span className="new-landing-page__sellers-buyers-text">
                  <span
                    className="new-landing-page__sellers-buyers-count"
                    data-loading={isCountsLoading ? 'true' : 'false'}
                  >
                    {countBody(isCountsLoading, t('newLandingPage.explore.sellersCount', { count: visibleCounts.sellers }))}
                  </span>
                  <span
                    className="new-landing-page__sellers-buyers-divider"
                    aria-hidden="true"
                  />
                  <span
                    className="new-landing-page__sellers-buyers-count"
                    data-loading={isCountsLoading ? 'true' : 'false'}
                  >
                    {countBody(isCountsLoading, t('newLandingPage.explore.buyersCount', { count: visibleCounts.buyers }))}
                  </span>
                </span>
                <ExpandAltIcon />
              </button>
              {!isLoggedIn && !isDesktopExplorePanel && sellingCta}
            </div>
          )}

          {!isBuildingViewOpen && (
            <div
              className="new-landing-page__desktop-explore-rail"
              role="group"
            >
              <button
                aria-busy={isGeolocating}
                aria-label={t('newLandingPage.explore.geolocateAria')}
                data-map-occluder="gps"
                className={`new-landing-page__desktop-explore-geolocate${isGeolocating ? ' new-landing-page__desktop-explore-geolocate--loading' : ''}`}
                disabled={isGeolocating}
                onClick={handleGeolocate}
                type="button"
              >
                <GeolocateArrowIcon />
              </button>
              <div
                data-map-occluder="zoom"
                className="new-landing-page__desktop-explore-zoom"
                role="group"
              >
                <button
                  aria-label={t('newLandingPage.explore.zoomInAria')}
                  className="new-landing-page__desktop-explore-zoom-button"
                  onClick={handleZoomIn}
                  type="button"
                >
                  <ZoomInIcon />
                </button>
                <span
                  className="new-landing-page__desktop-explore-zoom-divider"
                  aria-hidden="true"
                />
                <button
                  aria-label={t('newLandingPage.explore.zoomOutAria')}
                  className="new-landing-page__desktop-explore-zoom-button"
                  onClick={handleZoomOut}
                  type="button"
                >
                  <ZoomOutIcon />
                </button>
              </div>
            </div>
          )}

          {!isBuildingViewOpen && (!isLoggedIn || mapFeaturesTruncated) && (
            <div className="new-landing-page__desktop-explore-bottom">
              {zoomInHint}
              {!isLoggedIn && isDesktopExplorePanel && sellingCta}
            </div>
          )}

          {/* Kept mounted while the building view is open (JAA-324): the closed
              state is a transform + visibility, not display:none, so the browser
              preserves the list's scrollTop and Back lands where the user left. */}
          <div
              className="new-landing-page__mobile-info-sheet"
              data-open={
                showMobileInfoSheet && !isBuildingViewOpen ? 'true' : 'false'
              }
              style={
                mobileSheetDragOffset > 0
                  ? ({
                      transform: `translateY(${mobileSheetDragOffset}px)`,
                      transition: 'none',
                    } as React.CSSProperties)
                  : undefined
              }
              aria-hidden={!(showMobileInfoSheet && !isBuildingViewOpen)}
            >
              <div className="new-landing-page__desktop-explore-info-header">
                <div className="new-landing-page__desktop-explore-info-header-top">
                  <div className="new-landing-page__desktop-explore-info-location">
                    <LocationPinIcon />
                    <p className="new-landing-page__desktop-explore-info-area">
                      {areaText}
                    </p>
                  </div>
                  {postalText && (
                    <p className="new-landing-page__desktop-explore-info-postal">
                      {postalText}
                    </p>
                  )}
                </div>
                <div className="new-landing-page__desktop-explore-info-counts">
                  <button
                    className="new-landing-page__desktop-explore-info-counts-tab new-landing-page__sellers-buyers-count"
                    data-loading={isCountsLoading ? 'true' : 'false'}
                    data-active={mapFilter === 'sellers' ? 'true' : 'false'}
                    aria-pressed={mapFilter === 'sellers'}
                    onClick={() => handleToggleListingSide('sellers')}
                    type="button"
                  >
                    {countBody(isCountsLoading, t('newLandingPage.explore.sellersCount', { count: visibleCounts.sellers }))}
                  </button>
                  <button
                    className="new-landing-page__desktop-explore-info-counts-tab new-landing-page__sellers-buyers-count"
                    data-loading={isCountsLoading ? 'true' : 'false'}
                    data-active={mapFilter === 'buyers' ? 'true' : 'false'}
                    aria-pressed={mapFilter === 'buyers'}
                    onClick={() => handleToggleListingSide('buyers')}
                    type="button"
                  >
                    {countBody(isCountsLoading, t('newLandingPage.explore.buyersCount', { count: visibleCounts.buyers }))}
                  </button>
                </div>
              </div>
              <div
                className="new-landing-page__mobile-info-sheet-content"
                ref={mobileSheetContentRef}
              >
                {renderExploreContent('mobile')}
              </div>
            </div>

          {!isBuildingViewOpen && (
            <div
              data-map-occluder="results-panel"
              className="new-landing-page__desktop-explore-info-panel"
              aria-busy={isCountsLoading}
            >
              <div className="new-landing-page__panel-pane">
                <div className="new-landing-page__desktop-explore-info-header">
                  <div className="new-landing-page__desktop-explore-info-header-top">
                    <div className="new-landing-page__desktop-explore-info-location">
                      <LocationPinIcon />
                      <p className="new-landing-page__desktop-explore-info-area">
                        {areaText}
                      </p>
                    </div>
                    <div className="new-landing-page__desktop-explore-info-header-right">
                      {postalText && (
                        <p className="new-landing-page__desktop-explore-info-postal">
                          {postalText}
                        </p>
                      )}
                      {/* Panel collapse is disabled while map visibility scopes results. */}
                    </div>
                  </div>
                  <div className="new-landing-page__desktop-explore-info-counts">
                    <button
                      className="new-landing-page__desktop-explore-info-counts-tab new-landing-page__sellers-buyers-count"
                      data-loading={isCountsLoading ? 'true' : 'false'}
                      data-active={mapFilter === 'sellers' ? 'true' : 'false'}
                      aria-pressed={mapFilter === 'sellers'}
                      onClick={() => handleToggleListingSide('sellers')}
                      type="button"
                    >
                      {countBody(isCountsLoading, t('newLandingPage.explore.sellersCount', { count: visibleCounts.sellers }))}
                    </button>
                    <button
                      className="new-landing-page__desktop-explore-info-counts-tab new-landing-page__sellers-buyers-count"
                      data-loading={isCountsLoading ? 'true' : 'false'}
                      data-active={mapFilter === 'buyers' ? 'true' : 'false'}
                      aria-pressed={mapFilter === 'buyers'}
                      onClick={() => handleToggleListingSide('buyers')}
                      type="button"
                    >
                      {countBody(isCountsLoading, t('newLandingPage.explore.buyersCount', { count: visibleCounts.buyers }))}
                    </button>
                  </div>
                </div>
                <div
                  className="new-landing-page__desktop-explore-info-content"
                >
                  {renderExploreContent('desktop')}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div
        className="new-landing-page__search-overlay"
        aria-hidden={!isSearchActive}
        onClick={handleSearchDeactivate}
      />

      {!isLoggedIn && (
        <main className="new-landing-page__hero">
          <div className="new-landing-page__hero-content" ref={heroContentRef}>
            <h1 className="new-landing-page__headline">
              <span className="new-landing-page__decorated-word">
                {t('newLandingPage.hero.homes')}
                <img
                  alt=""
                  aria-hidden="true"
                  className="new-landing-page__headline-decoration new-landing-page__headline-decoration--home"
                  src={homeIcon}
                />
              </span>{' '}
              {t('newLandingPage.hero.and')}{' '}
              <span className="new-landing-page__decorated-word">
                {t('newLandingPage.hero.buyers')}
                <img
                  alt=""
                  aria-hidden="true"
                  className="new-landing-page__headline-decoration new-landing-page__headline-decoration--coin"
                  src={coinIcon}
                />
              </span>
              {/* hero.tail joins with NO space: FI leads with a comma, EN carries its own leading space (JAA-329) */}
              {t('newLandingPage.hero.tail')}
            </h1>

            <p className="new-landing-page__subheadline">
              {t('newLandingPage.hero.subheadline')}
            </p>

            <button
              className="new-landing-page__cta"
              onClick={handleStartBrowsing}
              type="button"
            >
              {t('newLandingPage.hero.startExploring')}
            </button>
          </div>
        </main>
      )}

      <AddMyPlaceFlow
        isOpen={addMyPlaceOpen}
        isLoggedIn={isLoggedIn}
        userEmail={userEmail || undefined}
        onClose={handleCloseAddMyPlace}
        onComplete={handleAddMyPlaceComplete}
        mapCenter={
          addMyPlaceSeed?.center ?? lastMapBoundaryBoxCenter ?? mapCenter
        }
        mapZoom={lastZoomLevel ?? 15}
        countryOfFeedContent={countryOfFeedContent}
        setLoginModalVisible={setLoginModalVisible}
        onToast={setToastMessage}
        onPresenceChange={setAddMyPlaceFlowPresent}
        lockBodyScroll={!isMapExpanded}
        seed={addMyPlaceSeed}
      />
      <CouldBuyFlow
        isOpen={couldBuySeed !== null}
        isLoggedIn={isLoggedIn}
        seed={couldBuySeed}
        fallbackCenter={couldBuyFallbackCenter}
        onToast={setToastMessage}
        onClose={() => {
          setCouldBuySeed(null);
          setCouldBuyInitialForm(undefined);
          setCouldBuyInterestId(undefined);
          setCouldBuyWillingnessId(undefined);
        }}
        onComplete={(flyTarget) => {
          if (flyTarget)
            mapHandleRef.current?.flyTo(flyTarget.center, flyTarget.zoom);
          setCouldBuySeed(null);
          setCouldBuyInitialForm(undefined);
          setCouldBuyInterestId(undefined);
          setCouldBuyWillingnessId(undefined);
        }}
        setLoginModalVisible={setLoginModalVisible}
        initialForm={couldBuyInitialForm}
        interestId={couldBuyInterestId}
        willingnessId={couldBuyWillingnessId}
        onPresenceChange={setCouldBuyFlowPresent}
        lockBodyScroll={!isMapExpanded}
      />

      {/* Oikotie-style: the card pops anchored on the map AND the panel drills
          into the same building (desktop). Mobile shows only the docked card.
          Both surfaces read the same `preview` service, so closing either via
          its close/back control clears both. */}
      {preview.building &&
        createPortal(
          <BuildingPreviewCard
            building={preview.building}
            listings={visiblePreview.listings}
            areaDemand={visiblePreview.areaDemand}
            hasFilteredListings={visiblePreview.hasFilteredListings}
            isLoading={preview.loading}
            buyerCount={preview.meta.buyers + preview.areaDemand.length}
            likeCount={preview.meta.likes}
            followerCount={preview.meta.followers}
            isLiked={preview.meta.liked}
            isDesktop={isDesktopExplorePanel}
            anchor={preview.anchor}
            lngLat={
              preview.building.centroid ||
              preview.building.addresses?.[0]?.coordinates ||
              preview.building.properties?.addresses?.[0]?.coordinates ||
              null
            }
            mapHandle={mapHandleRef}
            mapReady={Boolean(mapInstance)}
            isCovered={hideHeaderMapControls && !isDesktopExplorePanel}
            isLoggedIn={isLoggedIn}
            setLoginModalVisible={setLoginModalVisible}
            onClose={preview.close}
            onExpand={expandPreview}
            onOpenListing={openListing}
            onSellHere={openAddMyPlaceWithSeed}
            onToggleLike={handlePreviewToggleLike}
          />,
          document.body,
        )}
    </div>
    </>
  );
};

export default NewLandingPage;
